import { ApolloQueryResult } from 'apollo-client';
import { forEach, isEqual, isNumber, keyBy, keys, reduce, uniqBy, values } from 'lodash';
import moment from 'moment';

import {
    CampaignIdAndMetrikaCountersInput,
    UpdateCampaignAvailableForKeyGoalsPayload
} from '../../actions/campaignsEdit';
import { InitKeyGoalsEditorsItemData } from '../../actions/metrikaKeyGoals';
import { InitCampaignsRelevantPhrasesEditorsItemData } from '../../actions/relevantPhrases';
import { HOURS_PER_DAY, RATE_CORRECTION_DEFAULT_VALUE } from '../../constants';
import { selectVisitingCardEditorValue } from '../../selectors/visitingCardEditor/visitingCardEditor';
import { getDefaultVCardData } from '../../service/BannerHelpers/BannerHelpers';
import {
    convertToFlatStructureErrors,
    getCampaignEditPageEditorId,
    getCampaignMetrikaKeyGoalsEditorId,
    getDefaultCampaignName,
    getEnabledFieldsByType,
    mapPartialDataByFieldFromCampaigns
} from '../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { castStringToEnum } from '../../service/CastHelpers';
import { defaultExperimentsValue } from '../../service/ExperimentsEditorHelpers/ExperimentsEditorHelpers';
import { parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { getRandomId, getRandomNumericId } from '../../service/IdHelpers/IdHelpers';
import {
    mapEmailNotificationsType,
    mapNotificationCheckPositionIntervalFromApiFormat,
    mapNotificationCheckPositionIntervalToApiFormat,
    mapSmsNotificationsApiType,
    mapSmsNotificationsType,
    orderEmailNotifications,
    orderSmsNotifications,
    sortNotifications
} from '../../service/NotificationEditorHelper/NotificationEditorHelper';
import {
    formatMinuteTimeToStructuredTime,
    formatStructuredTimeToMinuteTime,
    formatTimeNumberToString
} from '../../service/TimeHelper/TimeHelper';
import {
    getDefaultTimeTargetingHolidaysSettings,
    getEndHourTimeFromHourIndexForTimeTargeting,
    getHourIndexFromEndHourTimeForTimeTargeting,
    getHoursRangeItemByRange,
    getHoursRangeItemForTimeTargeting,
    getListSelectedRowForTimeTargetingBoard,
    getSelectedDaysRangeForTimeTargeting,
    getTimeRangeByHoursRangeItem,
    getTimeTargetingBoardWithoutRateCorrectionsMode,
    hasTimeTargetingBoardItemsWitModifiedRateCorrections,
    TimeTargetingHoursSelectType
} from '../../service/TimeTargetingEditorHelper/TimeTargetingEditorHelper';
import { getNameTimeZoneGroupByGroupNick } from '../../service/TimeZoneHelper/TimeZoneHelper';
import { isNeedToUpdateEditorVisitingCard } from '../../service/VisitingCardEditorHelpers/VisitingCardEditorHelpers';
import { CampaignPlace } from '../../typings/campaignPlace';
import {
    ApiGoal,
    ApiGoalWithDomain,
    CampaignsEditEditorsValidationResult,
    DataForEditCampaigns,
    EditCampaignData,
    EditCampaignDataValue,
    EditCampaignEditor,
    EditCampaignsConstants,
    EditCampaignsErrorsData,
    EditCampaignValidationErrors,
    EditCampaignValueField,
    EnabledFieldsMap
} from '../../typings/campaignsEdit';
import { CampaignService } from '../../typings/campaignService';
import { ChatListValue } from '../../typings/chatList';
import { ConstData } from '../../typings/constants';
import { DatesIntervalEditorsState, DatesIntervalEditorState } from '../../typings/datesInterval';
import { CampaignPlatform, CampaignType } from '../../typings/domain';
import { ExperimentsValue, UpdateCampaignExperimentsValuePayload } from '../../typings/experimentsEditor';
import { AdditionalGoalIds, Goal } from '../../typings/goals';
import {
    BroadmatchGoalsQuery_client_broadMatchGoals_campaignGoals,
    BroadmatchGoalsQuery_client_broadMatchGoals_campaignGoals_goals,
    CampaignsABSegmentsQuery_client_abSegments_campaignGoals,
    CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals,
    CampaignsABSegmentsQueryForNewCampaign_client_abSegmentsByCounterIds_counterWithGoals,
    CampaignsABSegmentsQueryForNewCampaign_client_abSegmentsByCounterIds_counterWithGoals_goals,
    CampaignsAvailableForKeyGoalsForNewCampaignQuery_client_goalsByCounterIds_counterWithGoals,
    CampaignsAvailableForKeyGoalsQuery_client_meaningfulGoals_campaignGoals,
    CampaignsEditDataQuery,
    CampaignsEditDataQuery_client_campaigns_rowset,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentRetargetingCondition,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentStatisticRetargetingCondition,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_broadMatch,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_commonVcard,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_meaningfulGoals,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_strategy_GdCampaignStrategyRoi_budget,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_timeTarget,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_timeTarget_holidaysSettings,
    CampaignsEditDataQuery_client_clientConstants,
    CampaignsEditDataQuery_constants,
    CampaignsEditDataQuery_operator_access,
    DefaultCampaignsEditQuery,
    DefaultCampaignsEditQuery_constants_defaultValues_campaigns_commonVcard,
    DefaultCampaignsEditQuery_constants_defaultValues_campaigns_strategy,
    GdAddCampaignUnionInput,
    GdAddCampaignVcardInput,
    GdAddContentPromotionCampaignInput,
    GdAddCpmPriceCampaignInput,
    GdAddInternalAutobudgetCampaignInput,
    GdAddTextCampaignInput,
    GdAddVcardInput,
    GdAddVcardsInput,
    GdBroadMatchRequestInput,
    GdCampaignAttributionModelInput,
    GdCampaignBudgetPeriod,
    GdCampaignBudgetShowMode,
    GdCampaignEmailSettingsRequestInput,
    GdCampaignNotificationRequestInput,
    GdCampaignPlatform,
    GdCampaignPrimaryStatus,
    GdCampaignsAvailableFields,
    GdCampaignSmsEventInput,
    GdContentLanguage,
    GdContentLanguageInput,
    GdCreateRetargetingConditionForExperimentsItemInput,
    GdHolidaysSettingsInput,
    GdMeaningfulGoalRequestInput,
    GdPriceFlightViewTypeInput,
    GdTimeTargetInput,
    GdUpdateBidModifiersInput,
    GdUpdateCampaignUnionInput,
    GdUpdateContentPromotionCampaignInput,
    GdUpdateCpmPriceCampaignInput,
    GdUpdateInternalAutobudgetCampaignInput,
    GdUpdateTextCampaignInput,
    RetargetingConditionsQueryForCampaignsEdit_client_retargetingConditions_rowset,
    TimeZoneGroupsQuery
} from '../../typings/graphql-grid';
import { MetrikaSelectedKeyGoal } from '../../typings/metrikaKeyGoals';
import { MinusPhrasesValue } from '../../typings/minusPhrases';
import {
    CampaignEventsNotification,
    CampaignNotification,
    CampaignsNotificationApiValue,
    ChangeTrafficValueNotification,
    NotificationEditorState,
    NotificationSource,
    NotificationsValue,
    NotificationType,
    PercentOfCurrentBalanceNotification,
    StopByReachDailyBudgetNotification,
    XlsReadyNotification
} from '../../typings/notificationEditor';
import { PricePackageEditorValue } from '../../typings/pricePackages';
import { ABSegmentsRateCorrection, RetargetingConditionDataForRateCorrections } from '../../typings/ratesCorrections';
import { RelevantPhrasesValue } from '../../typings/relevantPhrases';
import { ShowsFrequencyPeriodPreset } from '../../typings/showsFrequency';
import { ClientFeatures, State } from '../../typings/state';
import { AttributionModel, DnaStrategyType, OptimizationCriteria, Strategy } from '../../typings/strategy';
import {
    TimeTargetingEditorHolidaySettings,
    TimeTargetingEditorState,
    TimeTargetingValue,
    TimeTargetingValueHolidaySettings,
    TimeZoneGroup
} from '../../typings/timeTargetingEditor';
import { Omit } from '../../typings/utils';
import { VisitingCardEditorState, VisitingCardValueEditCampaign } from '../../typings/VisitingCardEditor';
import { mapVCardForEditQueryResultToQueryResponse } from '../banners-edit/api-data-mappers';
import { mapVCardInfoToApiFormat, mapVCardToApiFormat } from '../banners-edit/state-data-mappers/state-data-mappers';
import {
    getNumberFromApiFormat,
    getStringFromApiFormat,
    getValueFromApiFormat,
    getValueToApiFormat,
    mapCampaignRowTypeToCampaignType,
    mapDateToApiFormat,
    mapMetrikaCountersToApiFormat,
    mapNumberStringToApiFormat
} from '../common/common';
import {
    getAvailableSegmentsMap,
    getCompleteABSegmentsCorrectionRatesMapFromApi,
    getCompleteArchivedMapFromApi,
    getCompleteSelectedMapFromApi,
    getExperimentsWithArchivedMap,
    getGroupedByExperimentSegments,
    mapExperimentsTree,
    mapSavedABSegmentsCorrectionRates,
    mapSavedSelectedSegments
} from '../experimentsEditor';
import {
    getAllowedRateCorrectionsDataForCampaign,
    mapABSegmentsRateCorrectionsData,
    mapABSegmentsRateCorrectionsToApi,
    mapRateCorrectionsData,
    mapRatesCorrectionsConstantsFromApiFormat,
    mapRatesCorrectionsValueToApiFormatForCampaign
} from '../rateCorrections/rateCorrections';
import { mapCampaignRelevantPhrasesValueToRelevantPhrasesEditorValue } from '../relevantPhrases';

import {
    mapAttributionModelFromApiFormat,
    mapAttributionModelToApiFormat,
    mapContextLimitToApiFormat,
    mapDayBudgetShowModeToApiFormat,
    mapDayBudgetToApiFormat,
    mapEnableCpcHoldToApiFormat,
    mapStrategyToApiFormat
} from './strategy';

export const mapDefaultCampaignsQueryResultToQueryResponse = (
    result: ApolloQueryResult<DefaultCampaignsEditQuery>,
    commonConstants: ConstData,
    clientFeatures: ClientFeatures,
    campaignType: CampaignType
): DataForEditCampaigns => {
    const {
        client: {
            clientConstants,
            notification: { smsPhone },
            campaignsCount,
            defaultCampaignNotifications
        },
        constants: {
            defaultValues: { campaigns: defaultCampaignsValue }
        }
    } = result.data;

    const [defaultCampaignValue] = defaultCampaignsValue;
    const [defaultCampaignNotification] = defaultCampaignNotifications;

    if (!defaultCampaignValue) {
        throw new Error(`Отсутствуют дефолтные значения для кампании "${campaignType}"`);
    }

    if (!defaultCampaignNotification) {
        throw new Error(`Отсутствуют дефолтные значения уведомлений для кампании "${campaignType}"`);
    }

    const editCampaignsConstants = mapEditCampaignConstants(result.data.constants, clientConstants, clientFeatures);

    const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForCampaign(
        clientConstants.allowedBidModifierTypesByCampaignTypes
    );
    // При маппинге ошибок в id сущности должны быть только числовые значения
    const campaignId = getRandomNumericId();

    const campaignValue: EditCampaignDataValue = {
        [EditCampaignValueField.name]: getDefaultCampaignName(campaignType, Number(campaignsCount)),
        [EditCampaignValueField.dates]: {
            startDate: moment(defaultCampaignValue.startDate),
            endDate: undefined
        },
        [EditCampaignValueField.allowedPageIds]: mapAllowedPageIdsFromApiFormat(defaultCampaignValue.allowedPageIds),
        [EditCampaignValueField.disabledIps]: defaultCampaignValue.disabledIps,
        [EditCampaignValueField.relevantPhrases]: mapCampaignRelevantPhrasesFromApiFormat(
            defaultCampaignValue.broadMatch,
            defaultCampaignValue.defaultBroadMatchAllGoalsId
        ),
        [EditCampaignValueField.chatOperator]: mapChatOperatorValueFromApi(defaultCampaignValue.clientDialogId),
        [EditCampaignValueField.metrikaCounters]: mapMetrikaCountersFromApiFormatEditCampaigns(
            defaultCampaignValue.metrikaCounters
        ),
        [EditCampaignValueField.experiments]: defaultExperimentsValue,
        [EditCampaignValueField.metrikaKeyGoals]: {
            selectedGoals: mapMetrikaKeyGoals(defaultCampaignValue.menaningfulGoals)
        },
        [EditCampaignValueField.markMetrikaLinks]: defaultCampaignValue.defaultAddMetrikaTagToUrl,
        [EditCampaignValueField.hasBannersTextSubstitution]: defaultCampaignValue.hasTitleSubstitute,
        [EditCampaignValueField.showYandexCatalogInfo]: defaultCampaignValue.defaultEnableCompanyInfo,
        [EditCampaignValueField.considerCompetitors]: !defaultCampaignValue.defaultExcludePausedCompetingAds,
        [EditCampaignValueField.markTargetCallLinks]: defaultCampaignValue.defaultAddOpenstatTagToUrl,
        // DIRECT-97267
        [EditCampaignValueField.bannersLanguage]: getValueFromApiFormat(defaultCampaignValue.contentLanguage),
        [EditCampaignValueField.notifications]: mapCampaignsNotificationFromApiFormat(
            defaultCampaignNotification,
            commonConstants,
            false,
            defaultCampaignValue.hasSiteMonitoring,
            getValueFromApiFormat(smsPhone)
        ),
        [EditCampaignValueField.strategy]: mapCampaignStrategyFromApiFormat(
            defaultCampaignValue.strategy,
            mapAttributionModelFromApiFormat(defaultCampaignValue.attributionModel),
            defaultCampaignValue.defaultHasEnableCpcHold,
            defaultCampaignValue.defaultContextLimit,
            editCampaignsConstants,
            clientFeatures
        ),
        [EditCampaignValueField.service]: mapCampaignServiceFromApiFormat(),
        [EditCampaignValueField.place]: mapCampaignPlaceFromApiFormat(),
        [EditCampaignValueField.bannedDomains]: mapDisabledPlacesToBannedDomains(defaultCampaignValue.disabledPlaces),
        [EditCampaignValueField.siteMonitoring]: defaultCampaignValue.hasSiteMonitoring,
        [EditCampaignValueField.minusPhrases]: {
            minusKeywords: []
        },
        [EditCampaignValueField.timeTargeting]: mapTimeTargetingFromApi(defaultCampaignValue.defaultTimeTarget),
        [EditCampaignValueField.rateCorrections]: mapRateCorrectionsData(
            defaultCampaignValue.bidModifiers,
            allowedRateCorrectionsData[campaignType]
        ),
        [EditCampaignValueField.hasExtendedGeoTargeting]: defaultCampaignValue.hasExtendedGeoTargeting,
        [EditCampaignValueField.visitingCard]: mapVisitingCardForEditCampaignsFromApiFormat(
            defaultCampaignValue.commonVcard
        ),
        [EditCampaignValueField.isAloneTrafaretAllowed]: defaultCampaignValue.defaultIsAloneTrafaretAllowed,
        [EditCampaignValueField.pricePackageId]: '',
        [EditCampaignValueField.datesWithRequiredEndDate]: {
            startDate: moment(defaultCampaignValue.startDate),
            endDate: moment(defaultCampaignValue.startDate)
        },
        // TODO: Стабы showsFrequency
        [EditCampaignValueField.showsFrequency]: {
            isActive: false,
            showsLimit: '333',
            periodPreset: ShowsFrequencyPeriodPreset.Period,
            customPeriod: '11'
        }
    };

    return {
        campaigns: {
            [campaignId]: {
                id: String(campaignId),
                exportId: '',
                access: {
                    canEdit: true
                },
                type: campaignType,
                value: campaignValue,
                serverValue: campaignValue,
                isStopped: false,
                hasNotModeratedBanners: false,
                areOtherSettingsVisible: false,
                relevantPhrasesGoals: {
                    isMetrikaAvailable: true,
                    goalsData: {}
                },
                availableForKeyGoals: {},
                isAvailableForKeyGoalsLoading: true,
                enabledFields: mapAccessSettingsToEnabledFields(
                    campaignType,
                    result.data.operator.access,
                    clientFeatures
                ),
                abSegments: {
                    isMetrikaAvailable: true,
                    isLoading: true
                },
                strategyGoals: {
                    isMetrikaAvailable: true,
                    goalsData: {},
                    turboGoalsData: {},
                    isLoading: true
                },
                errors: {}
            }
        },
        constants: editCampaignsConstants
    };
};

export const mapCampaignsForEditQueryResultToQueryResponse = (
    result: ApolloQueryResult<CampaignsEditDataQuery>,
    commonConstants: ConstData,
    clientFeatures: ClientFeatures
): DataForEditCampaigns => {
    const {
        client: {
            campaigns,
            clientConstants,
            notification: { smsPhone }
        },
        operator: { access },
        constants
    } = result.data;
    const campaignsRows = campaigns.rowset;
    const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForCampaign(
        clientConstants.allowedBidModifierTypesByCampaignTypes
    );

    const editCampaignsConstants = mapEditCampaignConstants(constants, clientConstants, clientFeatures);

    return {
        campaigns: campaignsRows.reduce(
            (
                acc: Record<string, EditCampaignData>,
                campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
            ): Record<string, EditCampaignData> => {
                const campaignId = String(campaignRow.id);
                const campaignType = mapCampaignRowTypeToCampaignType(campaignRow.type);

                const value = {
                    [EditCampaignValueField.name]: campaignRow.name,
                    [EditCampaignValueField.dates]: {
                        startDate: moment(campaignRow.startDate),
                        endDate: campaignRow.endDate ? moment(campaignRow.endDate) : undefined
                    },
                    [EditCampaignValueField.allowedPageIds]: mapAllowedPageIdsFromApiFormatRow(campaignRow),
                    [EditCampaignValueField.disabledIps]: mapDisabledIpsFromApiFormat(campaignRow),
                    [EditCampaignValueField.relevantPhrases]: mapCampaignRelevantPhrasesFromApiFormatRow(
                        campaignRow,
                        constants.defaultValues.campaign.defaultBroadMatchAllGoalsId
                    ),
                    [EditCampaignValueField.chatOperator]: mapChatOperatorValueFromApiRow(campaignRow),
                    [EditCampaignValueField.metrikaCounters]: mapMetrikaCountersFromApiFormatEditCampaignsRow(
                        campaignRow
                    ),
                    [EditCampaignValueField.experiments]: mapSavedExperimentsFromApiRow(
                        campaignRow,
                        mapABSegmentsRateCorrectionsData(campaignRow.bidModifiers)
                    ),
                    [EditCampaignValueField.metrikaKeyGoals]: {
                        selectedGoals: mapMetrikaKeyGoalsRow(campaignRow)
                    },
                    [EditCampaignValueField.markMetrikaLinks]: mapMarkMetrikaLinksFromApiFormatRow(campaignRow),
                    [EditCampaignValueField.hasBannersTextSubstitution]: mapHasTitleSubstitutionFromApiFormatRow(
                        campaignRow
                    ),
                    [EditCampaignValueField.showYandexCatalogInfo]: mapShowYandexCatalogInfoFromApiFormatRow(
                        campaignRow
                    ),
                    [EditCampaignValueField.considerCompetitors]: mapConsiderCompetitorsFromApiFormatRow(campaignRow),
                    [EditCampaignValueField.markTargetCallLinks]: mapMarkTargetCallLinksFromApiFormatRow(campaignRow),
                    // DIRECT-97267
                    [EditCampaignValueField.bannersLanguage]: mapBannerLanguageRow(campaignRow),
                    [EditCampaignValueField.notifications]: mapCampaignsNotificationFromApiFormat(
                        campaignRow.notification,
                        commonConstants,
                        Boolean(campaignRow.endDate),
                        campaignRow.status.allowDomainMonitoring,
                        getValueFromApiFormat(smsPhone)
                    ),
                    [EditCampaignValueField.strategy]: mapCampaignStrategyFromApiFormatRow(
                        campaignRow,
                        editCampaignsConstants,
                        clientFeatures
                    ),
                    [EditCampaignValueField.service]: mapCampaignServiceFromApiFormat(campaignRow),
                    [EditCampaignValueField.place]: mapCampaignPlaceFromApiFormat(campaignRow),
                    [EditCampaignValueField.bannedDomains]: mapDisabledPlacesToBannedDomainsRow(campaignRow),
                    [EditCampaignValueField.siteMonitoring]: campaignRow.status.allowDomainMonitoring,
                    [EditCampaignValueField.minusPhrases]: mapMinusKeywordsFromApiFormat(campaignRow),
                    [EditCampaignValueField.timeTargeting]: mapTimeTargetingFromApiRow(campaignRow),
                    [EditCampaignValueField.rateCorrections]: mapRateCorrectionsData(
                        campaignRow.bidModifiers,
                        allowedRateCorrectionsData[campaignType]
                    ),
                    [EditCampaignValueField.hasExtendedGeoTargeting]: mapHasExtendedGeoTargetingFromApiFormatRow(
                        campaignRow
                    ),
                    [EditCampaignValueField.visitingCard]: mapVisitingCardForEditCampaignsFromApiFormatRow(campaignRow),
                    [EditCampaignValueField.organizationId]: mapOrganizationIdForEditCampaigns(campaignRow),
                    [EditCampaignValueField.isAloneTrafaretAllowed]: mapIsAloneTrafaretAllowedFromApiFormatRow(
                        campaignRow
                    ),
                    [EditCampaignValueField.pricePackageId]: mapCampaignPricePricePackagesFromApiFormatRow(campaignRow),
                    [EditCampaignValueField.datesWithRequiredEndDate]: {
                        startDate: moment(campaignRow.startDate),
                        endDate: campaignRow.endDate ? moment(campaignRow.endDate) : moment(Date.now())
                    },
                    // TODO: Стабы showsFrequency
                    [EditCampaignValueField.showsFrequency]: {
                        isActive: false,
                        showsLimit: '333',
                        periodPreset: ShowsFrequencyPeriodPreset.Period,
                        customPeriod: '11'
                    }
                };
                acc[campaignId] = {
                    id: campaignId,
                    access: {
                        canEdit: campaignRow.access.canEdit
                    },
                    exportId: String(campaignRow.exportId),
                    type: mapCampaignRowTypeToCampaignType(campaignRow.type),
                    value: value,
                    serverValue: value,
                    isStopped: [
                        GdCampaignPrimaryStatus.STOPPED,
                        GdCampaignPrimaryStatus.DRAFT,
                        GdCampaignPrimaryStatus.ARCHIVED
                    ].includes(campaignRow.status.primaryStatus),
                    hasNotModeratedBanners: Boolean(campaignRow.status.hasRunningUnmoderatedAds),
                    areOtherSettingsVisible: false,
                    availableForKeyGoals: {},
                    isAvailableForKeyGoalsLoading: true,
                    enabledFields: mapAccessSettingsToEnabledFields(
                        mapCampaignRowTypeToCampaignType(campaignRow.type),
                        access,
                        clientFeatures
                    ),
                    errors: {},
                    abSegments: {
                        // Сначала поверяется доступность условий ретаргетинга из аудиторий, на которые будут маппиться сегменты
                        isMetrikaAvailable: mapExperimentsRetargetingConditionsAvailability(campaignRow),
                        isLoading: true
                    },
                    strategyGoals: {
                        isMetrikaAvailable: true,
                        goalsData: {},
                        turboGoalsData: {},
                        isLoading: true
                    }
                };

                return acc;
            },
            {}
        ),
        constants: editCampaignsConstants
    };
};

export const mapExperimentsRetargetingConditionsAvailability = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            const { abSegmentRetargetingCondition, abSegmentStatisticRetargetingCondition } = campaignRow;

            if (abSegmentRetargetingCondition !== null) {
                return Boolean(abSegmentRetargetingCondition.isMetrikaAvailable);
            } else if (abSegmentStatisticRetargetingCondition !== null) {
                return Boolean(abSegmentStatisticRetargetingCondition.isMetrikaAvailable);
            }

            return true;
        default:
            return true;
    }
};

function apiGoalToDomain(goal: ApiGoal, parent: ApiGoal | undefined): Goal {
    const hasDomain = (obj: ApiGoal): obj is ApiGoalWithDomain => {
        return 'domain' in obj;
    };

    let domain;
    if (hasDomain(goal)) {
        domain = getValueFromApiFormat(goal.domain);
    } else {
        domain = '';
    }

    return {
        id: goal.id,
        name: parent ? `${parent.simpleName} → ${goal.simpleName}` : goal.simpleName,
        domain
    };
}

export const mapCampaignBroadMatchGoalsFromApiFormat = (
    broadMatchGoalsData: BroadmatchGoalsQuery_client_broadMatchGoals_campaignGoals[] | null,
    campaignId: string
): Record<string, Goal> => {
    if (broadMatchGoalsData === null) {
        return {};
    }

    const campaignGoalsInfoFromApi = broadMatchGoalsData.find(item => {
        return String(item.campaignId) === campaignId;
    });

    if (!campaignGoalsInfoFromApi) {
        return {};
    }

    const goalsMap = keyBy(campaignGoalsInfoFromApi.goals, 'id');
    return campaignGoalsInfoFromApi.goals.reduce(
        (
            res: Record<string, Goal>,
            goal: BroadmatchGoalsQuery_client_broadMatchGoals_campaignGoals_goals
        ): Record<string, Goal> => {
            res[goal.id] = apiGoalToDomain(goal, goal.parentId ? goalsMap[goal.parentId] : undefined);

            return res;
        },
        {}
    );
};

export const mapRetargetingConditionCampaignsEditFromApiFormat = (
    conditionFromApi: RetargetingConditionsQueryForCampaignsEdit_client_retargetingConditions_rowset
): RetargetingConditionDataForRateCorrections => {
    const { retargetingConditionId, name } = conditionFromApi;

    return {
        id: retargetingConditionId,
        name: name
    };
};

export const mapSavedExperimentsFromApiRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset,
    rateCorrections: ABSegmentsRateCorrection
): ExperimentsValue => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            return mapSavedExperimentsFromApi(
                campaignRow.abSegmentRetargetingCondition,
                campaignRow.abSegmentStatisticRetargetingCondition,
                rateCorrections
            );
        default:
            return defaultExperimentsValue;
    }
};

const mapSavedExperimentsFromApi = (
    abSegmentRetargetingCondition: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentRetargetingCondition | null,
    abSegmentStatisticRetargetingCondition: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentStatisticRetargetingCondition | null,
    rateCorrections: ABSegmentsRateCorrection
): ExperimentsValue => {
    let savedSelectedSegmentsIds: string[] = [];
    let abSegmentRetargetingConditionId: string | undefined;
    let abSegmentStatisticRetargetingConditionId: string | undefined;
    let archivedExperimentsMap: Record<number, boolean> = {};

    if (abSegmentRetargetingCondition !== null && abSegmentStatisticRetargetingCondition !== null) {
        const { retargetingCondition } = abSegmentRetargetingCondition;
        const { retargetingCondition: statisticRetargeingCondition } = abSegmentStatisticRetargetingCondition;

        // Метрика недоступна, флаг abSegments.isMetrikaAvailable выставлен в false
        if (retargetingCondition === null || statisticRetargeingCondition === null) {
            return defaultExperimentsValue;
        }

        // Включен таргетинг по сегментам
        abSegmentRetargetingConditionId = retargetingCondition.retargetingConditionId;
        abSegmentStatisticRetargetingConditionId = statisticRetargeingCondition.retargetingConditionId;

        savedSelectedSegmentsIds = mapSavedSelectedSegments(retargetingCondition.conditionRules);

        archivedExperimentsMap = getExperimentsWithArchivedMap(retargetingCondition);
    } else if (abSegmentStatisticRetargetingCondition !== null) {
        const { retargetingCondition: statisticRetargeingCondition } = abSegmentStatisticRetargetingCondition;

        // Метрика недоступна, флаг abSegments.isMetrikaAvailable выставлен в false
        if (statisticRetargeingCondition === null) {
            return defaultExperimentsValue;
        }

        // Таргетинг по сегментам отключен, но эксперименты активны
        abSegmentStatisticRetargetingConditionId = statisticRetargeingCondition.retargetingConditionId;

        savedSelectedSegmentsIds = mapSavedSelectedSegments(statisticRetargeingCondition.conditionRules);

        archivedExperimentsMap = getExperimentsWithArchivedMap(statisticRetargeingCondition);
    } else {
        // Эксперименты неактивны
        savedSelectedSegmentsIds = [];
    }

    return {
        ...defaultExperimentsValue,
        savedSelectedSegmentsIds,
        areCorrectionsEnabled: Boolean(rateCorrections.isSwitchedOn),
        isSegmentTargetingEnabled: abSegmentRetargetingCondition !== null,
        correctionsMap: mapSavedABSegmentsCorrectionRates(rateCorrections.items),
        abSegmentRetargetingConditionId,
        abSegmentStatisticRetargetingConditionId,
        archivedExperimentsMap
    };
};

export const mapVisitingCardForEditCampaignsFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): VisitingCardValueEditCampaign => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return mapVisitingCardForEditCampaignsFromApiFormat(campaignRow.commonVcard);
    }

    return getDefaultVcardForEditCampaigns();
};

export const mapVisitingCardForEditCampaignsFromApiFormat = (
    commonVcard:
        | DefaultCampaignsEditQuery_constants_defaultValues_campaigns_commonVcard
        | CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_commonVcard
        | null
): VisitingCardValueEditCampaign => {
    const defaultVcard = getDefaultVcardForEditCampaigns();

    if (!commonVcard) {
        return defaultVcard;
    }

    const info = mapVCardForEditQueryResultToQueryResponse(commonVcard);

    return {
        ...defaultVcard,
        info: {
            ...info
        },
        vCardId: commonVcard.id ? String(commonVcard.id) : getRandomId(), // для визитки важно иметь id
        isNotShowMetroInSearchChecked: info.metroStationId === '0'
    };
};

export const mapOrganizationIdForEditCampaigns = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string | undefined => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            return getValueFromApiFormat(campaignRow.defaultPermalinkId);
        default:
            return undefined;
    }
};

export const mapBannerLanguageRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): GdContentLanguage | undefined => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
        case 'GdContentPromotionCampaign':
            return getValueFromApiFormat(campaignRow.contentLanguage);
        default:
            return undefined;
    }
};

export const mapAccessSettingsToEnabledFields = (
    campaignType: CampaignType,
    operatorAccess: CampaignsEditDataQuery_operator_access,
    features: ClientFeatures
): EnabledFieldsMap => {
    const enabledFields = getEnabledFieldsByType(campaignType);

    if (!features.hasAbSegments) {
        enabledFields[EditCampaignValueField.experiments] = false;
    }

    if (!features.isAloneTrafaretOptionEnabled) {
        enabledFields[EditCampaignValueField.isAloneTrafaretAllowed] = false;
    }

    if (!features.isTycoonOrganizationsEnabled || !features.isCampaignDefaultOrganizationsEnabled) {
        enabledFields.organizationId = false;
    }

    const operatorAccessFieldsDictionary = {
        [GdCampaignsAvailableFields.CONTENT_LANGUAGE]: EditCampaignValueField.bannersLanguage,
        [GdCampaignsAvailableFields.ALLOWED_PAGE_IDS]: EditCampaignValueField.allowedPageIds
    };

    return Object.keys(operatorAccessFieldsDictionary).reduce((res, accessDependentField) => {
        const field = castStringToEnum(accessDependentField, GdCampaignsAvailableFields);
        const fieldValue = operatorAccessFieldsDictionary[field];

        if (enabledFields[fieldValue]) {
            return {
                ...res,
                [fieldValue]: operatorAccess.availableFields.campaignFields.includes(field)
            };
        }

        return res;
    }, enabledFields);
};

export const mapMinusKeywordsFromApiFormat = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): MinusPhrasesValue => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
        case 'GdContentPromotionCampaign':
            return {
                minusKeywords: campaignRow.minusKeywords || []
            };
        default:
            return {
                minusKeywords: []
            };
    }
};

export const mapEditCampaignConstants = (
    commonConstants: CampaignsEditDataQuery_constants,
    clientConstants: CampaignsEditDataQuery_client_clientConstants,
    clientFeatures: ClientFeatures
): EditCampaignsConstants => {
    const {
        validation: {
            campaignConstants: {
                maxNameLength,
                maxDisabledPlacesCount,
                maxMetrikaCountersNumberForTextCampaign,
                minBroadMatchLimit,
                maxBroadMatchLimit,
                maxDisabledIpsCount,
                roiCoefMin
            },
            minusKeywordsConstants: { normalizedMinusKeywordsMaxLength }
        },
        strategyConstants: { byAllGoalsGoalId, meaningfulGoalsOptimizationGoalId, payForConversionSumToAvgCpaMinRatio },
        defaultValues: {
            campaign: {
                defaultMeaningfulGoalId,
                defaultBroadMatchAllGoalsId,
                defaultContextLimit,
                defaultDayBudget,
                defaultHasEnableCpcHold
            }
        }
    } = commonConstants;
    // Стаб maxBannedVideoDomainsCount
    const maxBannedVideoDomainsCount = 20;

    return {
        maxNameLength,
        involvedSessionsId: String(defaultMeaningfulGoalId),
        maxBannedDomainsCount: maxDisabledPlacesCount,
        maxBannedVideoDomainsCount: maxBannedVideoDomainsCount,
        rateCorrectionsConstants: mapRatesCorrectionsConstantsFromApiFormat(
            clientConstants.allowedBidModifiersValues,
            Boolean(clientFeatures.isCpcDeviceModifiersAllowed)
        ),
        allowedRateCorrectionsData: getAllowedRateCorrectionsDataForCampaign(
            clientConstants.allowedBidModifierTypesByCampaignTypes
        ),
        maxMetrikaCountersNumberForTextCampaign,
        maxDisabledIpsCount,
        maxAllowedPageIdsCount: 1000, // перейти на использование константы от бэка, которая будет сделана в DIRECT-103248
        minRelevantPhrasesLimit: minBroadMatchLimit,
        maxRelevantPhrasesLimit: maxBroadMatchLimit,
        defaultRelevantPhrasesAllGoalsId: defaultBroadMatchAllGoalsId,
        roiCoefMin: Number(roiCoefMin),
        byAllGoalsGoalId,
        meaningfulGoalsOptimizationGoalId,
        payForConversionSumToAvgCpaMinRatio,
        defaultContextLimit,
        defaultDayBudget: Number(defaultDayBudget),
        defaultHasEnableCpcHold,
        normalizedMinusKeywordsMaxLength
    };
};

export const mapMetrikaKeyGoalsRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): MetrikaSelectedKeyGoal[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
            return mapMetrikaKeyGoals(campaignRow.meaningfulGoals);
        default:
            return [];
    }
};

export const mapMetrikaKeyGoals = (
    meaningfulGoals: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_meaningfulGoals[] | null
): MetrikaSelectedKeyGoal[] => {
    if (!meaningfulGoals) {
        return [];
    }

    return meaningfulGoals.map(goal => {
        const conversionValue = getValueFromApiFormat(goal.conversionValue);
        return {
            id: String(goal.goalId),
            conversionValue: conversionValue === undefined ? '' : String(conversionValue),
            errors: {
                goal: [],
                conversionValue: []
            }
        };
    });
};

export const mapChatOperatorValueFromApiRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): ChatListValue => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign': {
            return mapChatOperatorValueFromApi(campaignRow.clientDialogId);
        }

        default:
            return {
                selectedChatId: undefined
            };
    }
};

export const mapTimeTargetingHolidaysSettingsFromApiFormat = (
    holidaysSettings: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_timeTarget_holidaysSettings | null
): TimeTargetingValueHolidaySettings | undefined => {
    if (!holidaysSettings) {
        return undefined;
    }

    const { isShow, startHour, endHour, rateCorrections } = holidaysSettings;

    return {
        isShow: isShow,
        startHour: getValueFromApiFormat(startHour),
        endHour: getValueFromApiFormat(endHour),
        rateCorrections: getValueFromApiFormat(rateCorrections)
    };
};

export const mapTimeTargetingHolidaysSettingsToApiFormat = (
    holidaysSettings?: TimeTargetingValueHolidaySettings
): GdHolidaysSettingsInput | null => {
    if (!holidaysSettings) {
        return null;
    }

    const { isShow, startHour, endHour, rateCorrections } = holidaysSettings;

    return {
        isShow: isShow,
        startHour: getValueToApiFormat(startHour),
        endHour: getValueToApiFormat(endHour),
        rateCorrections: getValueToApiFormat(rateCorrections)
    };
};

export const mapTimeTargetingToApiFormat = (timeTargeting: TimeTargetingValue): GdTimeTargetInput => {
    const { timeBoard, useWorkingWeekends, holidaysSettings, enabledHolidaysMode, idTimeZone } = timeTargeting;

    return {
        enabledHolidaysMode,
        holidaysSettings: mapTimeTargetingHolidaysSettingsToApiFormat(holidaysSettings),
        idTimeZone,
        timeBoard,
        useWorkingWeekends
    };
};

export const mapTimeTargetingFromApiRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): TimeTargetingValue => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
        case 'GdCpmBannerCampaign':
        case 'GdContentPromotionCampaign': {
            return mapTimeTargetingFromApi(campaignRow.timeTarget);
        }

        case 'GdPriceCampaign': {
            return getDefaultTimeTargeting();
        }

        default:
            throw new Error(`Не поддерживаемый тип кампании: ${campaignRow.__typename}`);
    }
};

export const mapTimeTargetingFromApi = (
    timeTarget: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_timeTarget
): TimeTargetingValue => {
    const { timeBoard, useWorkingWeekends, enabledHolidaysMode, holidaysSettings, idTimeZone } = timeTarget;

    return {
        timeBoard,
        useWorkingWeekends,
        enabledHolidaysMode: enabledHolidaysMode,
        holidaysSettings: mapTimeTargetingHolidaysSettingsFromApiFormat(holidaysSettings),
        idTimeZone: String(idTimeZone)
    };
};

export const mapChatOperatorValueFromApi = (clientDialogId: GraphQlLong | null): ChatListValue => {
    return {
        selectedChatId: getStringFromApiFormat(clientDialogId)
    };
};

export const mapDisabledPlacesToBannedDomainsRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign': {
            return mapDisabledPlacesToBannedDomains(campaignRow.disabledPlaces);
        }
        default:
            return [];
    }
};

export const mapDisabledPlacesToBannedDomains = (disabledPlaces: string[] | null): string[] => {
    return getValueFromApiFormat(disabledPlaces) || [];
};

export const mapShowYandexCatalogInfoFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.enableCompanyInfo;
    }

    return false;
};

export const mapMarkMetrikaLinksFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasAddMetrikaTagToUrl;
    }

    return false;
};

export const mapMarkTargetCallLinksFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasAddOpenstatTagToUrl;
    }

    return false;
};

export const mapConsiderCompetitorsFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return !campaignRow.excludePausedCompetingAds;
    }

    return false;
};

export const mapHasTitleSubstitutionFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.hasTitleSubstitution;
    }

    return false;
};

export const mapHasExtendedGeoTargetingFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
        case 'GdContentPromotionCampaign':
            return campaignRow.hasExtendedGeoTargeting;
        default:
            return false;
    }
};

export const mapIsAloneTrafaretAllowedFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): boolean => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return campaignRow.isAloneTrafaretAllowed;
    }

    return false;
};

export const mapCampaignPricePricePackagesFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string => {
    switch (campaignRow.__typename) {
        case 'GdPriceCampaign': {
            return campaignRow.pricePackageId ? String(campaignRow.pricePackageId) : '';
        }
        default:
            return '';
    }
};

export const mapCampaignRelevantPhrasesFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset,
    defaultBroadMatchAllGoalsId: number
): RelevantPhrasesValue => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        const apiFormatData = campaignRow.broadMatch as CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_broadMatch;

        return mapCampaignRelevantPhrasesFromApiFormat(apiFormatData, defaultBroadMatchAllGoalsId);
    }

    return {
        isEnabled: false,
        limit: 100,
        goalId: AdditionalGoalIds.all
    };
};

export const mapCampaignRelevantPhrasesFromApiFormat = (
    broadMatch: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_broadMatch,
    defaultBroadMatchAllGoalsId: number
): RelevantPhrasesValue => {
    const { broadMatchLimit, broadMatchGoalId, broadMatchFlag } = broadMatch;

    let goalId;

    if (broadMatchGoalId === null) {
        goalId = AdditionalGoalIds.notSet;
    } else if (String(broadMatchGoalId) === String(defaultBroadMatchAllGoalsId)) {
        goalId = AdditionalGoalIds.all;
    } else {
        goalId = String(broadMatchGoalId);
    }

    return {
        isEnabled: broadMatchFlag,
        limit: broadMatchLimit,
        goalId: goalId
    };
};

export const mapAllowedPageIdsFromApiFormatRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] | undefined => {
    if (campaignRow.__typename === 'GdTextCampaign') {
        return mapAllowedPageIdsFromApiFormat(campaignRow.allowedPageIds);
    }

    return undefined;
};

export const mapAllowedPageIdsFromApiFormat = (allowedPageIds: GraphQlLong[] | null): string[] | undefined => {
    const preparedAllowedPageIds = getValueFromApiFormat(allowedPageIds);

    return preparedAllowedPageIds && preparedAllowedPageIds.map(String);
};

export const mapDisabledIpsFromApiFormat = (campaignRow: CampaignsEditDataQuery_client_campaigns_rowset): string[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
        case 'GdContentPromotionCampaign':
            const disabledIps = getValueFromApiFormat(campaignRow.disabledIps);

            return (disabledIps && disabledIps.map(String)) || [];
        default:
            return [];
    }
};

export const mapAllowedPageIdsToApiFormat = (allowedPageIds?: string[]): string[] | null => {
    if (allowedPageIds === undefined) {
        return null;
    }

    return allowedPageIds;
};

export const mapMetrikaCountersFromApiFormatEditCampaignsRow = (
    campaignRow: CampaignsEditDataQuery_client_campaigns_rowset
): string[] => {
    switch (campaignRow.__typename) {
        case 'GdTextCampaign':
        case 'GdContentPromotionCampaign':
            return mapMetrikaCountersFromApiFormatEditCampaigns(campaignRow.metrikaCounters);
        default:
            return [];
    }
};

export const mapMetrikaCountersFromApiFormatEditCampaigns = (metrikaCounters: number[] | null): string[] => {
    return metrikaCounters
        ? metrikaCounters.reduce((res, counter) => {
              if (counter === null) {
                  return res;
              }

              return [...res, counter.toString()];
          }, [])
        : [];
};

export const mapDatesIntervalEditorsDataFromCampaigns = (
    campaignsData: DataForEditCampaigns
): DatesIntervalEditorsState => {
    const { campaigns } = campaignsData;

    return Object.keys(campaigns).reduce((acc, campaignId) => {
        const currentCampaign = campaigns[campaignId];

        const datesValue = currentCampaign.value[EditCampaignValueField.dates];

        if (datesValue && currentCampaign.enabledFields.dates) {
            acc[campaignId] = {
                startDate: datesValue.startDate,
                endDate: datesValue.endDate
            };
        }

        return acc;
    }, {});
};

export const mapDatesIntervalRequiredEndDateEditorsDataFromCampaigns = (
    campaignsData: DataForEditCampaigns
): DatesIntervalEditorsState => {
    const { campaigns } = campaignsData;

    return Object.keys(campaigns).reduce((acc, campaignId) => {
        const currentCampaign = campaigns[campaignId];

        const datesValue = currentCampaign.value[EditCampaignValueField.datesWithRequiredEndDate];

        if (datesValue && currentCampaign.enabledFields.datesWithRequiredEndDate) {
            acc[campaignId] = {
                startDate: datesValue.startDate,
                endDate: datesValue.endDate
            };
        }

        return acc;
    }, {});
};

export const mapCampaignsToFetchGoalsInput = (
    campaignsRecord: Record<string, EditCampaignData>,
    editCampaignFieldCheck: EditCampaignValueField,
    newCounters?: string[]
): CampaignIdAndMetrikaCountersInput[] => {
    return mapPartialDataByFieldFromCampaigns<CampaignIdAndMetrikaCountersInput>(
        campaignsRecord,
        editCampaignFieldCheck,
        currentCampaign => {
            return {
                campaignId: currentCampaign.id,
                counterIds: newCounters || currentCampaign.value[EditCampaignValueField.metrikaCounters]
            };
        }
    );
};

export const mapCampaignsIdsToFetchVisitingCardInput = (
    campaignsRecord: Record<string, EditCampaignData>
): string[] => {
    return mapPartialDataByFieldFromCampaigns<string>(
        campaignsRecord,
        EditCampaignValueField.visitingCard,
        campaign => campaign.id
    );
};

export const mapCampaignsIdsToFetchBroadMatchGoalsInput = (
    campaignsRecord: Record<string, EditCampaignData>
): string[] => {
    return mapPartialDataByFieldFromCampaigns<string>(
        campaignsRecord,
        EditCampaignValueField.relevantPhrases,
        campaign => campaign.id
    );
};

export const mapCampaignsVisitingCardIds = (campaignsRecord: Record<string, EditCampaignData>): string[] => {
    return Object.keys(campaignsRecord).reduce((acc: string[], campaignId) => {
        const currentCampaign = campaignsRecord[campaignId];
        const visitingCardValue = currentCampaign.value[EditCampaignValueField.visitingCard];
        const { vCardId } = visitingCardValue;

        if (vCardId) {
            acc.push(vCardId);
        }

        return acc;
    }, []);
};

export const mapMeaningfulGoalsQueryResultToGoalsAvailableForKeyGoals = (
    campaignGoalsFromApi: CampaignsAvailableForKeyGoalsQuery_client_meaningfulGoals_campaignGoals[] | null
): UpdateCampaignAvailableForKeyGoalsPayload[] => {
    if (!campaignGoalsFromApi) {
        return [];
    }

    return campaignGoalsFromApi.map(campaignGoal => {
        const goalsMap = keyBy(campaignGoal.goals, 'id');
        return {
            campaignId: campaignGoal.campaignId,
            goals: campaignGoal.goals.reduce((acc, goal) => {
                return {
                    ...acc,
                    [goal.id]: apiGoalToDomain(goal, goal.parentId ? goalsMap[goal.parentId] : undefined)
                };
            }, {})
        };
    });
};

export const mapGoalsByCounterIdsQueryResultToGoalsAvailableForKeyGoals = (
    counterWithGoals:
        | CampaignsAvailableForKeyGoalsForNewCampaignQuery_client_goalsByCounterIds_counterWithGoals[]
        | null,
    campaignId: string
): UpdateCampaignAvailableForKeyGoalsPayload => {
    if (counterWithGoals === null) {
        return {
            campaignId,
            goals: {}
        };
    }

    return {
        campaignId,
        goals: counterWithGoals.reduce((goalsResult, goalData) => {
            const goalsMap = keyBy(goalData.goals, 'id');

            return {
                ...goalsResult,
                ...goalData.goals.reduce((goalResult, goal) => {
                    return {
                        ...goalResult,
                        [goal.id]: apiGoalToDomain(goal, goal.parentId ? goalsMap[goal.parentId] : undefined)
                    };
                }, {})
            };
        }, {})
    };
};

export const mapABSegmentsQueryResultToCampaignExperimentsValue = (
    campaignGoalsFromApi: CampaignsABSegmentsQuery_client_abSegments_campaignGoals[] | null,
    campaigns: Record<string, EditCampaignData>
): UpdateCampaignExperimentsValuePayload[] => {
    if (campaignGoalsFromApi === null || campaignGoalsFromApi.length === 0) {
        return [];
    }

    return campaignGoalsFromApi.map(campaignGoalsData => {
        return mapABSegmentsToCampaignExperimentsValue(
            campaignGoalsData.campaignId,
            campaignGoalsData.goals,
            campaigns
        );
    });
};

export const mapABSegmentsQueryForNewCampaignResultToCampaignExperimentsValue = (
    campaignId: string,
    countersWithGoals: CampaignsABSegmentsQueryForNewCampaign_client_abSegmentsByCounterIds_counterWithGoals[] | null,
    campaigns: Record<string, EditCampaignData>
): UpdateCampaignExperimentsValuePayload[] => {
    if (countersWithGoals === null || countersWithGoals.length === 0) {
        return [];
    }

    const allGoals = countersWithGoals.reduce((acc, counter) => {
        return [...acc, ...counter.goals];
    }, []);

    return [
        mapABSegmentsToCampaignExperimentsValue(
            campaignId,
            uniqBy(allGoals, goal => {
                return goal.id;
            }),
            campaigns
        )
    ];
};

export const mapABSegmentsToCampaignExperimentsValue = (
    campaignId: string,
    goals:
        | CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals[]
        | CampaignsABSegmentsQueryForNewCampaign_client_abSegmentsByCounterIds_counterWithGoals_goals[]
        | null,
    campaigns: Record<string, EditCampaignData>
): UpdateCampaignExperimentsValuePayload => {
    const experimentsValue = campaigns[campaignId].value[EditCampaignValueField.experiments];

    const groupedSegments = getGroupedByExperimentSegments(goals);
    const archivedExperimentsMap = getCompleteArchivedMapFromApi(
        groupedSegments,
        experimentsValue.archivedExperimentsMap
    );
    const experimentsTree = mapExperimentsTree(groupedSegments, archivedExperimentsMap);
    const availableSegmentsMap = getAvailableSegmentsMap(goals);
    const selectedMap = getCompleteSelectedMapFromApi(
        experimentsValue.savedSelectedSegmentsIds,
        availableSegmentsMap,
        experimentsTree,
        archivedExperimentsMap
    );

    return {
        ...experimentsValue,
        campaignId,
        experimentsTree,
        selectedMap,
        isSegmentTargetingEnabled: experimentsValue.isSegmentTargetingEnabled,
        areCorrectionsEnabled: experimentsValue.areCorrectionsEnabled,
        correctionsMap: getCompleteABSegmentsCorrectionRatesMapFromApi(
            availableSegmentsMap,
            experimentsValue.correctionsMap
        ),
        archivedExperimentsMap
    };
};

export const getNotificationsEditorDataFromNotificationsValue = (
    notificationValue: NotificationsValue
): NotificationEditorState => {
    const { smsInterval } = notificationValue;

    return {
        ...notificationValue,
        smsInterval: {
            ...smsInterval,
            inputStartTime: formatTimeNumberToString(smsInterval.startTime),
            inputEndTime: formatTimeNumberToString(smsInterval.endTime)
        },
        isOpen: false,
        isChangedPhone: false
    };
};

export const getNotificationsValueFromNotificationsEditor = (
    notificationEditorData: NotificationEditorState
): NotificationsValue => {
    const { email, phone, smsInterval, notifications } = notificationEditorData;

    return {
        email,
        phone,
        notifications: notifications.map(item => {
            const { errors, ...otherParams } = item;

            return {
                ...otherParams
            };
        }),
        smsInterval: {
            startTime: smsInterval.startTime,
            endTime: smsInterval.endTime
        }
    };
};

export const mapCampaignsNotificationFromApiFormat = (
    notification: CampaignsNotificationApiValue,
    constants: ConstData,
    hasEndDate: boolean,
    siteMonitoring: boolean,
    smsPhone?: string
): NotificationsValue => {
    const {
        defaultValues: {
            campaign: {
                defaultWarningBalance,
                defaultCheckPositionInterval,
                defaultSmsTimeInterval: { startTime: defaultStartTime, endTime: defaultEndTime }
            }
        }
    } = constants;
    const {
        smsSettings: {
            smsTime: { endTime = defaultEndTime, startTime = defaultStartTime },
            events: smsEvents
        },
        emailSettings: {
            email,
            allowedEvents: emailAllowedEvents,
            checkPositionInterval,
            sendAccountNews,
            stopByReachDailyBudget,
            warningBalance,
            xlsReady
        }
    } = notification;

    let smsNotifications: CampaignNotification[] = smsEvents.map(itemEvent => {
        const typeSmsEvent: NotificationType = mapSmsNotificationsType[itemEvent.event];

        const commonEventData = {
            source: NotificationSource.sms,
            value: itemEvent.checked
        };

        switch (typeSmsEvent) {
            case NotificationType.currentBalance:
                return {
                    ...commonEventData,
                    type: NotificationType.currentBalance,
                    disabled: !smsPhone
                };
            case NotificationType.endCampaign:
                return {
                    ...commonEventData,
                    type: NotificationType.endCampaign,
                    disabled: !hasEndDate || !smsPhone
                };
            case NotificationType.moderationResults:
                return {
                    ...commonEventData,
                    type: NotificationType.moderationResults,
                    disabled: !smsPhone
                };
            case NotificationType.monitoringResults:
                return {
                    ...commonEventData,
                    type: NotificationType.monitoringResults,
                    disabled: !smsPhone || !siteMonitoring
                };
            case NotificationType.increaseBalance:
                return {
                    ...commonEventData,
                    type: NotificationType.increaseBalance,
                    disabled: !smsPhone
                };
            case NotificationType.stopByReachDailyBudget:
                return {
                    ...commonEventData,
                    type: NotificationType.stopByReachDailyBudget,
                    disabled: !smsPhone
                };
            default:
                throw new Error(`Неизвестный тип sms уведомления: ${itemEvent.event}`);
        }
    });

    smsNotifications = sortNotifications(smsNotifications, orderSmsNotifications);

    let emailNotifications: CampaignNotification[] = emailAllowedEvents.map(allowedEventType => {
        const typeEmailEvent = mapEmailNotificationsType[allowedEventType];

        switch (typeEmailEvent) {
            case NotificationType.changeTrafficValue:
                const intervalValue = checkPositionInterval || defaultCheckPositionInterval;

                const changeTrafficValueNotification: ChangeTrafficValueNotification = {
                    source: NotificationSource.email,
                    value: Boolean(checkPositionInterval),
                    type: NotificationType.changeTrafficValue,
                    interval: mapNotificationCheckPositionIntervalFromApiFormat[intervalValue],
                    disabled: false
                };

                return changeTrafficValueNotification;

            case NotificationType.campaignEvents:
                const campaignEventsNotification: CampaignEventsNotification = {
                    source: NotificationSource.email,
                    value: Boolean(sendAccountNews),
                    type: NotificationType.campaignEvents,
                    disabled: false
                };

                return campaignEventsNotification;

            case NotificationType.stopByReachDailyBudget:
                const stopByReachDailyBudgetNotification: StopByReachDailyBudgetNotification = {
                    source: NotificationSource.email,
                    value: Boolean(stopByReachDailyBudget),
                    type: NotificationType.stopByReachDailyBudget,
                    disabled: false
                };

                return stopByReachDailyBudgetNotification;

            case NotificationType.percentOfCurrentBalance:
                const percentOfCurrentBalanceNotification: PercentOfCurrentBalanceNotification = {
                    source: NotificationSource.email,
                    value: Boolean(warningBalance),
                    type: NotificationType.percentOfCurrentBalance,
                    percent: warningBalance ? String(warningBalance) : String(defaultWarningBalance),
                    disabled: true
                };

                return percentOfCurrentBalanceNotification;

            case NotificationType.xlsReady:
                const xlsReadyNotification: XlsReadyNotification = {
                    source: NotificationSource.email,
                    value: Boolean(xlsReady),
                    type: NotificationType.xlsReady,
                    disabled: false
                };

                return xlsReadyNotification;

            default:
                throw new Error(`Неизвестный тип email уведомления: ${allowedEventType}`);
        }
    });

    emailNotifications = sortNotifications(emailNotifications, orderEmailNotifications);

    return {
        smsInterval: {
            startTime: formatStructuredTimeToMinuteTime(startTime),
            endTime: formatStructuredTimeToMinuteTime(endTime)
        },
        email: email,
        phone: smsPhone,
        notifications: smsNotifications.concat(emailNotifications)
    };
};

export const mapCampaignsNotificationToApiFormat = (
    notificationsValue: NotificationsValue
): GdCampaignNotificationRequestInput => {
    const {
        smsInterval: { startTime, endTime },
        notifications,
        email
    } = notificationsValue;

    const smsNotifications = notifications.filter(notification => notification.source === NotificationSource.sms);

    const emailNotifications = notifications.filter(notification => notification.source === NotificationSource.email);

    const emailSettings: GdCampaignEmailSettingsRequestInput = {
        sendAccountNews: null,
        stopByReachDailyBudget: null,
        checkPositionInterval: null,
        xlsReady: null,
        warningBalance: null,
        email
    };

    emailNotifications.forEach(emailNotification => {
        switch (emailNotification.type) {
            case NotificationType.changeTrafficValue:
                const preparedInterval = mapNotificationCheckPositionIntervalToApiFormat[emailNotification.interval];

                if (preparedInterval !== undefined && emailNotification.value) {
                    emailSettings.checkPositionInterval = preparedInterval;
                }

                break;

            case NotificationType.campaignEvents:
                emailSettings.sendAccountNews = emailNotification.value;

                break;

            case NotificationType.stopByReachDailyBudget:
                emailSettings.stopByReachDailyBudget = emailNotification.value;

                break;

            case NotificationType.percentOfCurrentBalance:
                const preparedPercent = emailNotification.percent && parseNumber(emailNotification.percent);

                if (isNumber(preparedPercent) && emailNotification.value) {
                    emailSettings.warningBalance = preparedPercent;
                }

                break;

            case NotificationType.xlsReady:
                emailSettings.xlsReady = emailNotification.value;

                break;

            default:
                throw new Error(`Неизвестный тип email уведомления: ${emailNotification.type}`);
        }
    });

    return {
        smsSettings: {
            smsTime: {
                startTime: formatMinuteTimeToStructuredTime(startTime),
                endTime: formatMinuteTimeToStructuredTime(endTime)
            },
            enableEvents: smsNotifications.reduce((acc: GdCampaignSmsEventInput[], smsNotification) => {
                if (smsNotification.value) {
                    acc.push(mapSmsNotificationsApiType[smsNotification.type]);
                }

                return acc;
            }, [])
        },
        emailSettings: emailSettings
    };
};

enum serverFieldForCampaignUpdateMutation {
    id = 'id',
    name = 'name',
    startDate = 'startDate',
    endDate = 'endDate',
    notification = 'notification',
    hasTitleSubstitute = 'hasTitleSubstitute',
    biddingStategy = 'biddingStategy',
    metrikaCounters = 'metrikaCounters',
    disabledPlaces = 'disabledPlaces',
    hasSiteMonitoring = 'hasSiteMonitoring',
    contentLanguage = 'contentLanguage',
    dayBudget = 'dayBudget',
    dayBudgetShowMode = 'dayBudgetShowMode',
    contextLimit = 'contextLimit',
    enableCpcHold = 'enableCpcHold',
    attributionModel = 'attributionModel',
    hasExtendedGeoTargeting = 'hasExtendedGeoTargeting',
    bidModifiers = 'bidModifiers',
    abSegmentRetargetingConditionId = 'abSegmentRetargetingConditionId',
    abSegmentStatisticRetargetingConditionId = 'abSegmentStatisticRetargetingConditionId',
    vcard = 'vcard',
    meaningfulGoals = 'meaningfulGoals',
    clientDialogId = 'clientDialogId',
    disabledIps = 'disabledIps',
    allowedPageIds = 'allowedPageIds',
    hasAddMetrikaTagToUrl = 'hasAddMetrikaTagToUrl',
    hasAddOpenstatTagToUrl = 'hasAddOpenstatTagToUrl',
    excludePausedCompetingAds = 'excludePausedCompetingAds',
    enableCompanyInfo = 'enableCompanyInfo',
    minusKeywords = 'minusKeywords',
    broadMatch = 'broadMatch',
    timeTarget = 'timeTarget',
    isAloneTrafaretAllowed = 'isAloneTrafaretAllowed',
    defaultPermalinkId = 'defaultPermalinkId',
    pricePackageId = 'pricePackageId',
    datesWithRequiredEndDate = 'datesWithRequiredEndDate'
}

const serverFieldToClientField = {
    [serverFieldForCampaignUpdateMutation.name]: EditCampaignValueField.name,
    [serverFieldForCampaignUpdateMutation.startDate]: EditCampaignValueField.dates,
    [serverFieldForCampaignUpdateMutation.endDate]: EditCampaignValueField.dates,
    [serverFieldForCampaignUpdateMutation.notification]: EditCampaignValueField.notifications,
    [serverFieldForCampaignUpdateMutation.hasTitleSubstitute]: EditCampaignValueField.hasBannersTextSubstitution,
    [serverFieldForCampaignUpdateMutation.biddingStategy]: EditCampaignValueField.strategy,
    [serverFieldForCampaignUpdateMutation.dayBudget]: EditCampaignValueField.strategy,
    [serverFieldForCampaignUpdateMutation.dayBudgetShowMode]: EditCampaignValueField.strategy,
    [serverFieldForCampaignUpdateMutation.contextLimit]: EditCampaignValueField.strategy,
    [serverFieldForCampaignUpdateMutation.enableCpcHold]: EditCampaignValueField.strategy,
    [serverFieldForCampaignUpdateMutation.attributionModel]: EditCampaignValueField.strategy,
    [serverFieldForCampaignUpdateMutation.metrikaCounters]: EditCampaignValueField.metrikaCounters,
    [serverFieldForCampaignUpdateMutation.disabledPlaces]: EditCampaignValueField.bannedDomains,
    [serverFieldForCampaignUpdateMutation.hasSiteMonitoring]: EditCampaignValueField.siteMonitoring,
    [serverFieldForCampaignUpdateMutation.contentLanguage]: EditCampaignValueField.bannersLanguage,
    [serverFieldForCampaignUpdateMutation.bidModifiers]: EditCampaignValueField.rateCorrections,
    [serverFieldForCampaignUpdateMutation.hasExtendedGeoTargeting]: EditCampaignValueField.hasExtendedGeoTargeting,
    [serverFieldForCampaignUpdateMutation.abSegmentRetargetingConditionId]: EditCampaignValueField.experiments,
    [serverFieldForCampaignUpdateMutation.abSegmentStatisticRetargetingConditionId]: EditCampaignValueField.experiments,
    [serverFieldForCampaignUpdateMutation.broadMatch]: EditCampaignValueField.relevantPhrases,
    [serverFieldForCampaignUpdateMutation.meaningfulGoals]: EditCampaignValueField.metrikaKeyGoals,
    [serverFieldForCampaignUpdateMutation.clientDialogId]: EditCampaignValueField.chatOperator,
    [serverFieldForCampaignUpdateMutation.disabledIps]: EditCampaignValueField.disabledIps,
    [serverFieldForCampaignUpdateMutation.allowedPageIds]: EditCampaignValueField.allowedPageIds,
    [serverFieldForCampaignUpdateMutation.hasAddMetrikaTagToUrl]: EditCampaignValueField.markMetrikaLinks,
    [serverFieldForCampaignUpdateMutation.hasAddOpenstatTagToUrl]: EditCampaignValueField.markTargetCallLinks,
    [serverFieldForCampaignUpdateMutation.excludePausedCompetingAds]: EditCampaignValueField.considerCompetitors,
    [serverFieldForCampaignUpdateMutation.enableCompanyInfo]: EditCampaignValueField.showYandexCatalogInfo,
    [serverFieldForCampaignUpdateMutation.minusKeywords]: EditCampaignValueField.minusPhrases,
    [serverFieldForCampaignUpdateMutation.vcard]: EditCampaignValueField.visitingCard,
    [serverFieldForCampaignUpdateMutation.timeTarget]: EditCampaignValueField.timeTargeting,
    [serverFieldForCampaignUpdateMutation.isAloneTrafaretAllowed]: EditCampaignValueField.isAloneTrafaretAllowed,
    [serverFieldForCampaignUpdateMutation.defaultPermalinkId]: EditCampaignValueField.organizationId
};

const getTextCampaignFieldMapperDict = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants,
    isCreateCampaignMapping?: boolean
): Partial<GdUpdateTextCampaignInput> | Partial<GdAddTextCampaignInput> => {
    const {
        name,
        dates: { startDate, endDate },
        hasBannersTextSubstitution,
        bannersLanguage,
        bannedDomains,
        notifications,
        siteMonitoring,
        disabledIps,
        chatOperator: { selectedChatId },
        hasExtendedGeoTargeting,
        strategy,
        experiments,
        relevantPhrases,
        allowedPageIds,
        metrikaKeyGoals: { selectedGoals },
        metrikaCounters,
        showYandexCatalogInfo,
        markTargetCallLinks,
        markMetrikaLinks,
        considerCompetitors,
        visitingCard,
        timeTargeting,
        minusPhrases,
        isAloneTrafaretAllowed,
        organizationId
    } = campaign.value;

    return {
        ...(isCreateCampaignMapping ? undefined : { [serverFieldForCampaignUpdateMutation.id]: campaign.id }),
        [serverFieldForCampaignUpdateMutation.name]: name,
        [serverFieldForCampaignUpdateMutation.startDate]: mapDateToApiFormat(startDate),
        [serverFieldForCampaignUpdateMutation.endDate]: endDate ? mapDateToApiFormat(endDate) : null,
        [serverFieldForCampaignUpdateMutation.notification]: mapCampaignsNotificationToApiFormat(notifications),
        [serverFieldForCampaignUpdateMutation.hasTitleSubstitute]: hasBannersTextSubstitution,
        [serverFieldForCampaignUpdateMutation.biddingStategy]: mapStrategyToApiFormat(strategy, constants),
        [serverFieldForCampaignUpdateMutation.dayBudget]: mapDayBudgetToApiFormat(strategy, constants.defaultDayBudget),
        [serverFieldForCampaignUpdateMutation.dayBudgetShowMode]: mapDayBudgetShowModeToApiFormat(strategy),
        [serverFieldForCampaignUpdateMutation.contextLimit]: mapContextLimitToApiFormat(
            strategy,
            constants.defaultContextLimit
        ),
        [serverFieldForCampaignUpdateMutation.enableCpcHold]: mapEnableCpcHoldToApiFormat(
            strategy,
            constants.defaultHasEnableCpcHold
        ),
        [serverFieldForCampaignUpdateMutation.attributionModel]: mapAttributionModelToApiFormat(strategy),
        [serverFieldForCampaignUpdateMutation.metrikaCounters]: mapMetrikaCountersToApiFormat(metrikaCounters),
        [serverFieldForCampaignUpdateMutation.disabledPlaces]: bannedDomains,
        [serverFieldForCampaignUpdateMutation.hasSiteMonitoring]: siteMonitoring,
        [serverFieldForCampaignUpdateMutation.hasExtendedGeoTargeting]: hasExtendedGeoTargeting,
        [serverFieldForCampaignUpdateMutation.bidModifiers]: mapBidModifiersToApiFormat(
            campaign,
            isCreateCampaignMapping
        ),
        [serverFieldForCampaignUpdateMutation.contentLanguage]: mapBannerLanguageToApiFormat(bannersLanguage),
        [serverFieldForCampaignUpdateMutation.abSegmentRetargetingConditionId]:
            experiments.abSegmentRetargetingConditionId,
        [serverFieldForCampaignUpdateMutation.abSegmentStatisticRetargetingConditionId]:
            experiments.abSegmentStatisticRetargetingConditionId,
        [serverFieldForCampaignUpdateMutation.meaningfulGoals]: mapMeaningfulGoalsToApiFormat(selectedGoals),
        [serverFieldForCampaignUpdateMutation.clientDialogId]: mapClientDialogIdToApiFormat(selectedChatId),
        [serverFieldForCampaignUpdateMutation.disabledIps]: mapDisabledIpsToApiFormat(disabledIps),
        [serverFieldForCampaignUpdateMutation.vcard]: mapVCardToApiFormatForEditCampaigns(visitingCard),
        [serverFieldForCampaignUpdateMutation.allowedPageIds]: mapAllowedPageIdsToApiFormat(allowedPageIds),
        [serverFieldForCampaignUpdateMutation.hasAddMetrikaTagToUrl]: markMetrikaLinks,
        [serverFieldForCampaignUpdateMutation.hasAddOpenstatTagToUrl]: markTargetCallLinks,
        [serverFieldForCampaignUpdateMutation.excludePausedCompetingAds]: !considerCompetitors,
        [serverFieldForCampaignUpdateMutation.enableCompanyInfo]: showYandexCatalogInfo,
        [serverFieldForCampaignUpdateMutation.timeTarget]: mapTimeTargetingToApiFormat(timeTargeting),
        [serverFieldForCampaignUpdateMutation.minusKeywords]: minusPhrases.minusKeywords,
        [serverFieldForCampaignUpdateMutation.broadMatch]: mapRelevantPhrasesToApiFormat(
            relevantPhrases,
            constants.defaultRelevantPhrasesAllGoalsId
        ),
        [serverFieldForCampaignUpdateMutation.isAloneTrafaretAllowed]: isAloneTrafaretAllowed,
        [serverFieldForCampaignUpdateMutation.defaultPermalinkId]: organizationId
    };
};

export const mapAddTextCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): { textCampaign: GdAddTextCampaignInput } => {
    const fieldMapperDict: Partial<GdAddTextCampaignInput> = getTextCampaignFieldMapperDict(campaign, constants, true);

    return {
        textCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce<GdAddTextCampaignInput>(
            (res: GdAddTextCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (campaign.enabledFields[serverFieldToClientField[field]]) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            {} as GdAddTextCampaignInput
        )
    };
};

export const mapUpdateTextCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): { textCampaign: GdUpdateTextCampaignInput } => {
    const fieldMapperDict: Partial<GdUpdateTextCampaignInput> = getTextCampaignFieldMapperDict(
        campaign,
        constants,
        false
    );

    return {
        textCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce<GdUpdateTextCampaignInput>(
            (res: GdUpdateTextCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (
                    field === serverFieldForCampaignUpdateMutation.id ||
                    campaign.enabledFields[serverFieldToClientField[field]]
                ) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            {} as GdUpdateTextCampaignInput
        )
    };
};

export const mapAddPriceCampaignDataToApiFormat = (campaign: EditCampaignData): GdAddCampaignUnionInput => {
    const {
        name,
        datesWithRequiredEndDate: { startDate, endDate },
        pricePackageId,
        notifications
    } = campaign.value;

    const fieldMapperDict = {
        [serverFieldForCampaignUpdateMutation.name]: name,
        [serverFieldForCampaignUpdateMutation.startDate]: mapDateToApiFormat(startDate),
        [serverFieldForCampaignUpdateMutation.endDate]: endDate ? mapDateToApiFormat(endDate) : null,
        [serverFieldForCampaignUpdateMutation.pricePackageId]: pricePackageId
    };

    const serverFieldToClientFieldPriceCampaign = {
        ...serverFieldToClientField,
        [serverFieldForCampaignUpdateMutation.startDate]: EditCampaignValueField.datesWithRequiredEndDate,
        [serverFieldForCampaignUpdateMutation.endDate]: EditCampaignValueField.datesWithRequiredEndDate
    };

    const baseValueGdAddCpmPriceCampaignInput = {
        name: name,
        startDate: mapDateToApiFormat(startDate),
        pricePackageId: pricePackageId,
        attributionModel: GdCampaignAttributionModelInput.FIRST_CLICK,
        hasAddMetrikaTagToUrl: true,
        timeTarget: { idTimeZone: '131' },
        hasAddOpenstatTagToUrl: false,
        notification: mapCampaignsNotificationToApiFormat(notifications),
        flightOrderVolume: '1000',
        flightTargetingsSnapshot: {
            geoExpanded: ['225'],
            geoType: 3,
            behaviors: [],
            viewTypes: [GdPriceFlightViewTypeInput.DESKTOP, GdPriceFlightViewTypeInput.MOBILE]
        }
    };

    return {
        cpmPriceCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce(
            (res: GdAddCpmPriceCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (campaign.enabledFields[serverFieldToClientFieldPriceCampaign[field]]) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            baseValueGdAddCpmPriceCampaignInput as GdAddCpmPriceCampaignInput
        )
    };
};

export const mapUpdatePriceCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): GdUpdateCampaignUnionInput => {
    const {
        name,
        datesWithRequiredEndDate: { startDate, endDate },
        notifications
    } = campaign.value;

    const fieldMapperDict = {
        [serverFieldForCampaignUpdateMutation.name]: name,
        [serverFieldForCampaignUpdateMutation.startDate]: mapDateToApiFormat(startDate),
        [serverFieldForCampaignUpdateMutation.endDate]: endDate ? mapDateToApiFormat(endDate) : null
    };

    const serverFieldToClientFieldPriceCampaign = {
        ...serverFieldToClientField,
        [serverFieldForCampaignUpdateMutation.startDate]: EditCampaignValueField.datesWithRequiredEndDate,
        [serverFieldForCampaignUpdateMutation.endDate]: EditCampaignValueField.datesWithRequiredEndDate
    };

    const baseValueGdUpdateCpmPriceCampaignInput = {
        id: campaign.id,
        attributionModel: GdCampaignAttributionModelInput.FIRST_CLICK,
        hasAddMetrikaTagToUrl: true,
        timeTarget: { idTimeZone: '131' },
        hasAddOpenstatTagToUrl: false,
        notification: mapCampaignsNotificationToApiFormat(notifications),
        flightOrderVolume: '1000'
    };

    return {
        cpmPriceCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce(
            (res: GdUpdateCpmPriceCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (campaign.enabledFields[serverFieldToClientFieldPriceCampaign[field]]) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            baseValueGdUpdateCpmPriceCampaignInput as GdUpdateCpmPriceCampaignInput
        )
    };
};

const getContentPromotionCampaignFieldMapperDict = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants,
    isCreateCampaignMapping?: boolean
): Partial<GdUpdateContentPromotionCampaignInput> | Partial<GdAddContentPromotionCampaignInput> => {
    const {
        name,
        dates: { startDate, endDate },
        bannersLanguage,
        notifications,
        disabledIps,
        hasExtendedGeoTargeting,
        strategy,
        metrikaCounters,
        timeTargeting,
        minusPhrases
    } = campaign.value;

    return {
        ...(isCreateCampaignMapping ? undefined : { [serverFieldForCampaignUpdateMutation.id]: campaign.id }),
        [serverFieldForCampaignUpdateMutation.name]: name,
        [serverFieldForCampaignUpdateMutation.startDate]: mapDateToApiFormat(startDate),
        [serverFieldForCampaignUpdateMutation.endDate]: endDate ? mapDateToApiFormat(endDate) : null,
        [serverFieldForCampaignUpdateMutation.notification]: mapCampaignsNotificationToApiFormat(notifications),
        [serverFieldForCampaignUpdateMutation.biddingStategy]: mapStrategyToApiFormat(strategy, constants),
        [serverFieldForCampaignUpdateMutation.dayBudget]: mapDayBudgetToApiFormat(strategy, constants.defaultDayBudget),
        [serverFieldForCampaignUpdateMutation.dayBudgetShowMode]: mapDayBudgetShowModeToApiFormat(strategy),
        [serverFieldForCampaignUpdateMutation.attributionModel]: mapAttributionModelToApiFormat(strategy),
        [serverFieldForCampaignUpdateMutation.metrikaCounters]: mapMetrikaCountersToApiFormat(metrikaCounters),
        [serverFieldForCampaignUpdateMutation.hasExtendedGeoTargeting]: hasExtendedGeoTargeting,
        [serverFieldForCampaignUpdateMutation.bidModifiers]: mapBidModifiersToApiFormat(
            campaign,
            isCreateCampaignMapping
        ),
        [serverFieldForCampaignUpdateMutation.contentLanguage]: mapBannerLanguageToApiFormat(bannersLanguage),
        [serverFieldForCampaignUpdateMutation.disabledIps]: mapDisabledIpsToApiFormat(disabledIps),
        [serverFieldForCampaignUpdateMutation.timeTarget]: mapTimeTargetingToApiFormat(timeTargeting),
        [serverFieldForCampaignUpdateMutation.minusKeywords]: minusPhrases.minusKeywords
    };
};

export const mapAddContentPromotionCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): { contentPromotionCampaign: GdAddContentPromotionCampaignInput } => {
    const fieldMapperDict: Partial<GdAddContentPromotionCampaignInput> = getContentPromotionCampaignFieldMapperDict(
        campaign,
        constants,
        true
    );

    return {
        contentPromotionCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce<
            GdAddContentPromotionCampaignInput
        >(
            (res: GdAddContentPromotionCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (campaign.enabledFields[serverFieldToClientField[field]]) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            {} as GdAddContentPromotionCampaignInput
        )
    };
};

export const mapUpdateContentPromotionCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): { contentPromotionCampaign: GdUpdateContentPromotionCampaignInput } => {
    const fieldMapperDict: Partial<GdUpdateContentPromotionCampaignInput> = getContentPromotionCampaignFieldMapperDict(
        campaign,
        constants,
        false
    );

    return {
        contentPromotionCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce<
            GdUpdateContentPromotionCampaignInput
        >(
            (res: GdUpdateContentPromotionCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (
                    field === serverFieldForCampaignUpdateMutation.id ||
                    campaign.enabledFields[serverFieldToClientField[field]]
                ) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            {} as GdUpdateContentPromotionCampaignInput
        )
    };
};
const getInternalAutobudgetCampaignFieldMapperDict = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants,
    isCreateCampaignMapping?: boolean
): Partial<GdUpdateInternalAutobudgetCampaignInput> | Partial<GdAddInternalAutobudgetCampaignInput> => {
    return {
        ...(isCreateCampaignMapping ? undefined : { [serverFieldForCampaignUpdateMutation.id]: campaign.id })
    };
};

export const mapAddInternalAutobudgetCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): { internalAutobudgetCampaign: GdAddInternalAutobudgetCampaignInput } => {
    const fieldMapperDict: Partial<GdAddInternalAutobudgetCampaignInput> = getInternalAutobudgetCampaignFieldMapperDict(
        campaign,
        constants,
        true
    );

    return {
        internalAutobudgetCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce<
            GdAddInternalAutobudgetCampaignInput
        >(
            (res: GdAddInternalAutobudgetCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (campaign.enabledFields[serverFieldToClientField[field]]) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            {} as GdAddInternalAutobudgetCampaignInput
        )
    };
};

export const mapUpdateInternalAutobudgetCampaignDataToApiFormat = (
    campaign: EditCampaignData,
    constants: EditCampaignsConstants
): { internalAutobudgetCampaign: GdUpdateInternalAutobudgetCampaignInput } => {
    const fieldMapperDict: Partial<
        GdUpdateInternalAutobudgetCampaignInput
    > = getInternalAutobudgetCampaignFieldMapperDict(campaign, constants, false);

    return {
        internalAutobudgetCampaign: Object.keys(serverFieldForCampaignUpdateMutation).reduce<
            GdUpdateInternalAutobudgetCampaignInput
        >(
            (res: GdUpdateInternalAutobudgetCampaignInput, field: serverFieldForCampaignUpdateMutation) => {
                if (
                    field === serverFieldForCampaignUpdateMutation.id ||
                    campaign.enabledFields[serverFieldToClientField[field]]
                ) {
                    res[field] = fieldMapperDict[field];
                }

                return res;
            },
            {} as GdUpdateInternalAutobudgetCampaignInput
        )
    };
};

export const mapRelevantPhrasesToApiFormat = (
    value: RelevantPhrasesValue,
    defaultBroadMatchAllGoalsId: number
): GdBroadMatchRequestInput => {
    let goalId;

    if (value.goalId === AdditionalGoalIds.all) {
        goalId = String(defaultBroadMatchAllGoalsId);
    } else if (value.goalId === AdditionalGoalIds.notSet) {
        goalId = null;
    } else {
        goalId = value.goalId;
    }

    return {
        broadMatchFlag: value.isEnabled,
        broadMatchGoalId: goalId,
        broadMatchLimit: value.limit
    };
};

export const mapBannerLanguageToApiFormat = (language?: GdContentLanguage): GdContentLanguageInput | undefined => {
    return language !== undefined ? castStringToEnum(String(language), GdContentLanguageInput) : undefined;
};

export const mapMeaningfulGoalsToApiFormat = (
    selectedGoals: MetrikaSelectedKeyGoal[]
): GdMeaningfulGoalRequestInput[] => {
    return selectedGoals.map(goal => {
        return {
            goalId: goal.id,
            conversionValue: goal.conversionValue ? mapNumberStringToApiFormat(goal.conversionValue) : null
        };
    });
};

export const mapAddCampaignsDataToApiFormat = (
    campaigns: Record<string, EditCampaignData>,
    constants: EditCampaignsConstants
): Omit<GdAddCampaignUnionInput, 'cpmPriceCampaign'>[] => {
    return values(campaigns).reduce((res: GdAddCampaignUnionInput[], campaign: EditCampaignData) => {
        switch (campaign.type) {
            case CampaignType.TEXT:
                res.push(mapAddTextCampaignDataToApiFormat(campaign, constants));
                break;
            case CampaignType.CONTENT_PROMOTION:
                res.push(mapAddContentPromotionCampaignDataToApiFormat(campaign, constants));
                break;
            case CampaignType.INTERNAL_AUTOBUDGET:
                res.push(mapAddInternalAutobudgetCampaignDataToApiFormat(campaign, constants));
                break;
            case CampaignType.CPM_PRICE:
                res.push(mapAddPriceCampaignDataToApiFormat(campaign));
                break;

            default:
                break;
        }

        return res;
    }, []);
};

export const mapUpdateCampaignsDataToApiFormat = (
    campaigns: Record<string, EditCampaignData>,
    constants: EditCampaignsConstants
): Omit<GdUpdateCampaignUnionInput, 'cpmPriceCampaign'>[] => {
    return values(campaigns).reduce((res: GdUpdateCampaignUnionInput[], campaign: EditCampaignData) => {
        switch (campaign.type) {
            case CampaignType.TEXT:
                res.push(mapUpdateTextCampaignDataToApiFormat(campaign, constants));
                break;
            case CampaignType.CONTENT_PROMOTION:
                res.push(mapUpdateContentPromotionCampaignDataToApiFormat(campaign, constants));
                break;
            case CampaignType.INTERNAL_AUTOBUDGET:
                res.push(mapUpdateInternalAutobudgetCampaignDataToApiFormat(campaign, constants));
                break;
            case CampaignType.CPM_PRICE:
                res.push(mapUpdatePriceCampaignDataToApiFormat(campaign, constants));
                break;

            default:
                break;
        }

        return res;
    }, []);
};

export const mapCampaignsDataToExperimentsConditionsRequestInput = (
    campaignIds: string[],
    campaigns: Record<string, EditCampaignData>
): GdCreateRetargetingConditionForExperimentsItemInput[] => {
    return campaignIds.reduce((acc, campaignId) => {
        const campaign = campaigns[campaignId];

        const { isSegmentTargetingEnabled, experimentsTree, selectedMap } = campaign.value[
            EditCampaignValueField.experiments
        ];

        const activeExperimentsIds = experimentsTree.reduce((inputs, experiment) => {
            return selectedMap[experiment.id] ? [...inputs, experiment.id] : inputs;
        }, []);

        if (isSegmentTargetingEnabled) {
            const abSegmentGoalIds = experimentsTree.reduce((inputs, experiment) => {
                if (!selectedMap[experiment.id] || experiment.inner === null) {
                    return inputs;
                }

                return [
                    ...inputs,
                    ...experiment.inner.reduce((abSegments, segment) => {
                        return selectedMap[segment.id] ? [...abSegments, String(segment.id)] : abSegments;
                    }, [])
                ];
            }, []);

            return [
                ...acc,
                {
                    abSegmentGoalIds,
                    sectionIds: activeExperimentsIds
                }
            ];
        } else {
            return [
                ...acc,
                {
                    sectionIds: activeExperimentsIds
                }
            ];
        }
    }, []);
};

export const mapServerValidationResultForCampaignToEditorsValidationResult = (
    campaignErrors: EditCampaignValidationErrors,
    campaignId: string
): Array<CampaignsEditEditorsValidationResult> => {
    const result: Array<CampaignsEditEditorsValidationResult> = [];

    const errorsBaseData = {
        campaignId
    };

    keys(campaignErrors).forEach(campaignErrorField => {
        switch (campaignErrorField) {
            case EditCampaignValueField.name: {
                const nameErrors = campaignErrors[campaignErrorField];

                if (nameErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.nameEditor,
                        errors: nameErrors.errors || []
                    });
                }

                break;
            }

            case EditCampaignValueField.metrikaKeyGoals: {
                const keyGoalsErrors = campaignErrors[campaignErrorField];

                if (keyGoalsErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.metrikaKeyGoalsEditor,
                        keyGoalsValidationData: Object.keys(keyGoalsErrors).reduce((acc, key) => {
                            return {
                                ...acc,
                                [key]: {
                                    goal: keyGoalsErrors[key].errors || [],
                                    conversionValue: []
                                }
                            };
                        }, {})
                    });
                }

                break;
            }

            case EditCampaignValueField.notifications: {
                const notificationsErrors = campaignErrors[campaignErrorField];

                if (notificationsErrors) {
                    const commonNotificationsErrors = notificationsErrors.common;
                    const preparedCommonNotificationsErrors = keys(commonNotificationsErrors).reduce(
                        (acc, keyField) => {
                            if (commonNotificationsErrors[keyField] !== undefined) {
                                acc[keyField] = commonNotificationsErrors[keyField].errors;
                            }

                            return acc;
                        },
                        {}
                    );

                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.notificationsEditor,
                        errors: {
                            common: {
                                ...preparedCommonNotificationsErrors
                            },
                            notifications: [],
                            isValid: false
                        }
                    });
                }

                break;
            }

            case EditCampaignValueField.metrikaCounters: {
                const metrikaCountersErrors = campaignErrors[campaignErrorField];

                if (metrikaCountersErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.metrikaCounterEditor,
                        errors: metrikaCountersErrors.errors || []
                    });
                }

                break;
            }

            case EditCampaignValueField.bannedDomains: {
                const bannedDomainsErrors = campaignErrors[campaignErrorField];

                result.push({
                    ...errorsBaseData,
                    editor: EditCampaignEditor.bannedDomainsEditor,
                    errors: bannedDomainsErrors.errors
                });

                break;
            }

            case EditCampaignValueField.disabledIps: {
                const disabledIpsErrors = campaignErrors[campaignErrorField];

                result.push({
                    ...errorsBaseData,
                    editor: EditCampaignEditor.disabledIpsEditor,
                    errors: disabledIpsErrors.errors || []
                });
                break;
            }

            case EditCampaignValueField.bannersLanguage: {
                const bannersLanguageErrors = campaignErrors[campaignErrorField];

                if (bannersLanguageErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.bannersLanguage,
                        errors: bannersLanguageErrors.errors || []
                    });
                }

                break;
            }

            case EditCampaignValueField.minusPhrases: {
                const minusPhrasesErrors = campaignErrors[campaignErrorField];

                result.push({
                    ...errorsBaseData,
                    editor: EditCampaignEditor.minusPhrases,
                    errors: (minusPhrasesErrors && minusPhrasesErrors.errors) || []
                });
                break;
            }

            case EditCampaignValueField.strategy: {
                const strategyErrors = campaignErrors[campaignErrorField];

                if (strategyErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.strategyEditor,
                        errors: reduce(
                            strategyErrors,
                            (acc, value, key) => ({
                                ...acc,
                                [key]: value.errors
                            }),
                            {}
                        )
                    });
                }

                break;
            }

            case EditCampaignValueField.visitingCard: {
                const visitingCardErrors = campaignErrors[campaignErrorField];

                if (visitingCardErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.visitingCardEditor,
                        errors: convertToFlatStructureErrors(visitingCardErrors)
                    });
                }

                break;
            }

            case EditCampaignValueField.organizationId: {
                const organizationErrors = campaignErrors[campaignErrorField];

                if (organizationErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: EditCampaignEditor.organization,
                        errors: convertToFlatStructureErrors(organizationErrors)
                    });
                }

                break;
            }

            case 'common': {
                const commonErrors = campaignErrors[campaignErrorField];

                if (commonErrors) {
                    result.push({
                        ...errorsBaseData,
                        editor: 'general',
                        errors: commonErrors.errors || []
                    });
                }

                break;
            }

            default:
                break;
        }
    });

    return result;
};

export const mapCampaignsEditServerValidationResultToEditorsValidationResult = (
    updateCampaignsErrorsData: EditCampaignsErrorsData
): Array<CampaignsEditEditorsValidationResult> => {
    let editorsValidationResult: Array<CampaignsEditEditorsValidationResult> = [];

    forEach(updateCampaignsErrorsData.campaigns, (campaignErrors: EditCampaignValidationErrors, campaignId: string) => {
        editorsValidationResult = [
            ...editorsValidationResult,
            ...mapServerValidationResultForCampaignToEditorsValidationResult(campaignErrors, campaignId)
        ];
    });

    return editorsValidationResult;
};

export const mapCampaignStrategyPlatformFromApiFormat = (rawPlatform: GdCampaignPlatform): CampaignPlatform => {
    switch (rawPlatform) {
        case GdCampaignPlatform.BOTH:
            return CampaignPlatform.BOTH;

        case GdCampaignPlatform.CONTEXT:
            return CampaignPlatform.CONTEXT;

        default:
        case GdCampaignPlatform.SEARCH:
            return CampaignPlatform.SEARCH;
    }
};

export const mapWeeklyBudgetFromApiFormat = (
    rawBudget: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_strategy_GdCampaignStrategyRoi_budget | null
): number | undefined => {
    if (rawBudget && rawBudget.period === GdCampaignBudgetPeriod.WEEK) {
        return getNumberFromApiFormat(rawBudget.sum);
    }

    return;
};

export const mapDailyBudgetFromApiFormat = (
    rawBudget: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_strategy_GdCampaignStrategyRoi_budget | null
): {
    dailyBudgetMode: GdCampaignBudgetShowMode;
    dailyBudget?: number;
} => {
    if (rawBudget && rawBudget.period === GdCampaignBudgetPeriod.DAY) {
        return {
            dailyBudgetMode: rawBudget.showMode,
            dailyBudget: getNumberFromApiFormat(rawBudget.sum) || undefined
        };
    }

    return {
        dailyBudgetMode: GdCampaignBudgetShowMode.DEFAULT,
        dailyBudget: undefined
    };
};

export const mapCampaignStrategyFromApiFormatRow = (
    campaign: CampaignsEditDataQuery_client_campaigns_rowset,
    constants: EditCampaignsConstants,
    clientFeatures: ClientFeatures
): Strategy => {
    const { strategy: rawStrategy, hasEnableCpcHold, contextLimit } = campaign;

    let attributionModel: AttributionModel;

    switch (campaign.__typename) {
        case 'GdTextCampaign':
        case 'GdContentPromotionCampaign':
            attributionModel = mapAttributionModelFromApiFormat(campaign.attributionModel);
            break;
        default:
            attributionModel = AttributionModel.LAST_YANDEX_DIRECT_CLICK;
    }

    return mapCampaignStrategyFromApiFormat(
        rawStrategy,
        attributionModel,
        hasEnableCpcHold,
        contextLimit,
        constants,
        clientFeatures
    );
};

export const mapCampaignStrategyFromApiFormat = (
    rawStrategy: DefaultCampaignsEditQuery_constants_defaultValues_campaigns_strategy,
    attributionModel: AttributionModel,
    hasEnableCpcHold: boolean,
    contextLimit: number,
    constants: EditCampaignsConstants,
    clientFeatures: ClientFeatures
): Strategy => {
    const baseData = {
        attributionModel,
        platform: mapCampaignStrategyPlatformFromApiFormat(rawStrategy.platform),
        errorCount: 0
    };

    switch (rawStrategy.__typename) {
        case 'GdStrategyOptimizeClicks': {
            let optimizationCriteria;
            if (rawStrategy.clicksLimit) {
                optimizationCriteria = OptimizationCriteria.WEEK_BUNDLE;
            } else if (!rawStrategy.avgBid) {
                optimizationCriteria = OptimizationCriteria.WEEKLY_BUDGET;
            } else {
                optimizationCriteria = OptimizationCriteria.AVG_CLICK;
            }

            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CLICKS,
                optimizationCriteria,
                avgClickPrice: getNumberFromApiFormat(rawStrategy.avgBid),
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                weekBundle: getNumberFromApiFormat(rawStrategy.clicksLimit),
                maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                errors: {}
            };
        }

        case 'GdStrategyOptimizeConversions':
            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CPA,
                optimizationCriteria: rawStrategy.avgCpa
                    ? OptimizationCriteria.AVG_CPA
                    : OptimizationCriteria.WEEKLY_BUDGET,
                goalId: rawStrategy.goalId ? String(rawStrategy.goalId) : AdditionalGoalIds.all,
                avgCpaPrice: getNumberFromApiFormat(rawStrategy.avgCpa),
                maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                errors: {},
                isCpaPayForConversionEnabled: clientFeatures.isCpaPayForConversionsStrategyAllowed
                    ? Boolean(rawStrategy.payForConversion)
                    : false
            };

        case 'GdCampaignStrategyRoi':
            return {
                ...baseData,
                type: DnaStrategyType.ROI,
                goalId: mapRoiGoalIdFromApiFormat(rawStrategy.goalId, constants),
                roiCoef: getNumberFromApiFormat(rawStrategy.roiCoef) || 0,
                maxClickPrice: getNumberFromApiFormat(rawStrategy.bid),
                reserveReturn: getNumberFromApiFormat(rawStrategy.reserveReturn) || 0,
                profitability: getNumberFromApiFormat(rawStrategy.profitability) || 0,
                weeklyBudget: mapWeeklyBudgetFromApiFormat(rawStrategy.budget),
                errors: {}
            };

        case 'GdCampaignStrategyManual': {
            const isBothPlatformsStrategy = rawStrategy.platform === GdCampaignPlatform.BOTH;

            return {
                ...baseData,
                type: DnaStrategyType.MANUAL,
                ...mapDailyBudgetFromApiFormat(rawStrategy.budget),
                separateBidding: isBothPlatformsStrategy ? Boolean(rawStrategy.separateBidding) : true,
                contextLimit:
                    isBothPlatformsStrategy && !rawStrategy.separateBidding
                        ? contextLimit || constants.defaultContextLimit
                        : constants.defaultContextLimit,
                cpcHoldEnabled: hasEnableCpcHold,
                errors: {}
            };
        }

        // временный костыль для CPM_BANNER, чтобы не ломались стратегии
        case 'GdCampaignStrategyAvgCpm': {
            return {
                ...baseData,
                type: DnaStrategyType.OPTIMIZE_CLICKS,
                optimizationCriteria: OptimizationCriteria.WEEK_BUNDLE,
                avgClickPrice: 5,
                weeklyBudget: 300,
                weekBundle: 500,
                maxClickPrice: 500,
                errors: {}
            };
        }

        default:
            throw new Error(`unexpected strategyType ${rawStrategy.__typename}`);
    }
};

const mapCampaignServiceFromApiFormat = (
    campaign?: CampaignsEditDataQuery_client_campaigns_rowset
): CampaignService => {
    return {
        serviceId: campaign ? '' /* campaign.serviceId */ : '',
        isMobile: campaign ? false /* campaign.isMobile */ : false
    };
};

const mapCampaignPlaceFromApiFormat = (campaign?: CampaignsEditDataQuery_client_campaigns_rowset): CampaignPlace => {
    return {
        pageId: campaign ? '' /* campaign.pageId */ : '',
        placeId: campaign ? '' /* campaign.placeId */ : ''
    };
};

export const getTimeTargetingEditorDataFromTimeTargetingValue = (
    value: TimeTargetingValue
): TimeTargetingEditorState => {
    const { timeBoard, holidaysSettings, enabledHolidaysMode } = value;

    const enabledRateCorrectionsModeForHolidaysSettings =
        enabledHolidaysMode &&
        holidaysSettings &&
        isNumber(holidaysSettings.rateCorrections) &&
        holidaysSettings.rateCorrections !== RATE_CORRECTION_DEFAULT_VALUE;

    const enableRateCorrectionsMode =
        hasTimeTargetingBoardItemsWitModifiedRateCorrections(timeBoard) ||
        enabledRateCorrectionsModeForHolidaysSettings;
    const rateCorrections = enableRateCorrectionsMode ? RATE_CORRECTION_DEFAULT_VALUE : undefined;

    const listSelectedDaysRows = getListSelectedRowForTimeTargetingBoard(timeBoard);
    const currentHoursRangeItem = getHoursRangeItemForTimeTargeting(timeBoard);
    const currentDaysRangeItem = getSelectedDaysRangeForTimeTargeting(listSelectedDaysRows);
    const editorHolidaysSettings = getTimeTargetingEditorHolidaySettingsFromValue(
        enabledHolidaysMode,
        holidaysSettings
    );

    return {
        ...value,
        selectedDays: listSelectedDaysRows,
        isOpened: true,
        rateCorrections: rateCorrections,
        currentDaysRangeItem,
        currentHoursRangeItem,
        holidaysSettings: editorHolidaysSettings,
        enabledHolidaysMode,
        ...getTimeRangeByHoursRangeItem(currentHoursRangeItem)
    };
};

export const getTimeTargetingHolidaysSettingsValueFromTimeTargetingEditor = (
    enabledHolidaysMode: boolean,
    holidaysSettings?: TimeTargetingEditorHolidaySettings,
    generalRateCorrections?: number
): TimeTargetingValueHolidaySettings | undefined => {
    if (!holidaysSettings || !enabledHolidaysMode) {
        return undefined;
    }

    const { currentHoursRangeItem } = holidaysSettings;

    if (currentHoursRangeItem.id === TimeTargetingHoursSelectType.ARBITRARY) {
        throw new Error('пользовательский тип интервала времени не может быть установлен для настроек праздников');
    }

    if (!holidaysSettings.isShow) {
        return {
            isShow: holidaysSettings.isShow,
            startHour: undefined,
            endHour: undefined,
            rateCorrections: undefined
        };
    }

    if (generalRateCorrections === undefined) {
        return {
            isShow: holidaysSettings.isShow,
            startHour: currentHoursRangeItem.startHour,
            endHour: getEndHourTimeFromHourIndexForTimeTargeting(currentHoursRangeItem.endHour),
            rateCorrections: undefined
        };
    }

    return {
        rateCorrections: holidaysSettings.rateCorrections,
        isShow: holidaysSettings.isShow,
        startHour: currentHoursRangeItem.startHour,
        endHour: getEndHourTimeFromHourIndexForTimeTargeting(currentHoursRangeItem.endHour)
    };
};

export const getTimeTargetingTimeBoardValueFromTimeTargetingEditor = (
    timeBoard: number[][],
    rateCorrections?: number
): number[][] => {
    if (rateCorrections === undefined) {
        return getTimeTargetingBoardWithoutRateCorrectionsMode(timeBoard);
    }

    return timeBoard;
};

export const getTimeTargetingValueFromTimeTargetingEditor = (editor: TimeTargetingEditorState): TimeTargetingValue => {
    const {
        timeBoard,
        rateCorrections,
        holidaysSettings,
        enabledHolidaysMode,
        useWorkingWeekends,
        idTimeZone
    } = editor;

    return {
        timeBoard: getTimeTargetingTimeBoardValueFromTimeTargetingEditor(timeBoard, rateCorrections),
        holidaysSettings: getTimeTargetingHolidaysSettingsValueFromTimeTargetingEditor(
            enabledHolidaysMode,
            holidaysSettings,
            rateCorrections
        ),
        useWorkingWeekends,
        enabledHolidaysMode,
        idTimeZone
    };
};

export const getTimeTargetingEditorHolidaySettingsFromValue = (
    enabledHolidaysMode: boolean,
    holidaysSettings?: TimeTargetingValueHolidaySettings
): TimeTargetingEditorHolidaySettings => {
    if (holidaysSettings === undefined || !enabledHolidaysMode) {
        return getDefaultTimeTargetingHolidaysSettings();
    }

    const {
        startHour = 0,
        endHour = HOURS_PER_DAY,
        rateCorrections = RATE_CORRECTION_DEFAULT_VALUE,
        isShow
    } = holidaysSettings;

    const currentHoursRangeItem = getHoursRangeItemByRange({
        startHour: startHour,
        endHour: getHourIndexFromEndHourTimeForTimeTargeting(endHour)
    });

    return {
        currentHoursRangeItem,
        rateCorrections,
        isShow,
        ...getTimeRangeByHoursRangeItem(currentHoursRangeItem)
    };
};

export function mapCampaignsDataForEditToInitKeyGoalsData(
    campaigns: Record<string, EditCampaignData>
): InitKeyGoalsEditorsItemData[] {
    return Object.keys(campaigns).reduce(
        (res: InitKeyGoalsEditorsItemData[], campaignId: string): InitKeyGoalsEditorsItemData[] => {
            const campaignData = campaigns[campaignId];

            if (campaignData.enabledFields[EditCampaignValueField.metrikaKeyGoals]) {
                res.push({
                    editorId: getCampaignMetrikaKeyGoalsEditorId(campaignId),
                    savedValue: campaigns[campaignId].value[EditCampaignValueField.metrikaKeyGoals].selectedGoals
                });
            }

            return res;
        },
        []
    );
}

export const mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue = (
    editorValue: VisitingCardEditorState
): VisitingCardValueEditCampaign => {
    const { isShortVersionChecked, isNotShowMetroInSearchChecked, vCardData, vCardId } = editorValue;

    const hasVCardData = vCardData && !isEqual(vCardData, getDefaultVCardData());

    return {
        isShortVersionChecked,
        isNotShowMetroInSearchChecked,
        info: vCardData
            ? {
                  ...vCardData
              }
            : undefined,
        vCardId: hasVCardData ? vCardId : ''
    };
};

export const getVisitingCardRequestData = (
    state: State,
    editorIds: string[],
    campaignIds: string[]
): GdAddVcardsInput => {
    return {
        vcardAddItems: editorIds.reduce((acc: GdAddVcardInput[], editorId, index) => {
            const visitingCardEditorState = selectVisitingCardEditorValue(state, editorId);

            const isNeedToUpdate = isNeedToUpdateEditorVisitingCard(visitingCardEditorState);

            if (isNeedToUpdate) {
                const currentVisitingCardValue = mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue(
                    visitingCardEditorState
                );

                acc.push(mapVCardToApiFormat(currentVisitingCardValue, campaignIds[index]));
            }

            return acc;
        }, [])
    };
};

export function mapCampaignsDataForEditToInitRelevantPhrasesEditors(
    campaigns: Record<string, EditCampaignData>
): InitCampaignsRelevantPhrasesEditorsItemData[] {
    return Object.keys(campaigns).reduce(
        (
            res: InitCampaignsRelevantPhrasesEditorsItemData[],
            campaignId: string
        ): InitCampaignsRelevantPhrasesEditorsItemData[] => {
            const campaignData = campaigns[campaignId];

            if (campaignData.enabledFields[EditCampaignValueField.relevantPhrases]) {
                res.push({
                    editorId: getCampaignEditPageEditorId(campaignId),
                    value: mapCampaignRelevantPhrasesValueToRelevantPhrasesEditorValue(
                        campaignData.value[EditCampaignValueField.relevantPhrases]
                    )
                });
            }

            return res;
        },
        []
    );
}

const mapDisabledIpsToApiFormat = (disabledIps: string[]): string[] | null => {
    return disabledIps.length === 0 ? null : disabledIps;
};

const mapClientDialogIdToApiFormat = (selectedChatId?: string): string | null => {
    return selectedChatId ? selectedChatId : null;
};

const mapBidModifiersToApiFormat = (
    campaign: EditCampaignData,
    isCreateCampaignMapping?: boolean
): GdUpdateBidModifiersInput => {
    const { rateCorrections, experiments } = campaign.value;

    return {
        ...mapRatesCorrectionsValueToApiFormatForCampaign(
            rateCorrections,
            isCreateCampaignMapping ? '0' : campaign.id // TODO для создания в API campaignId обязательное поле
        ),
        bidModifierABSegment: mapABSegmentsRateCorrectionsToApi(campaign, experiments, isCreateCampaignMapping)
    };
};

export const mapVCardToApiFormatForEditCampaigns = (
    vCard: VisitingCardValueEditCampaign
): GdAddCampaignVcardInput | null => {
    const { info, isNotShowMetroInSearchChecked } = vCard;

    if (!info || isEqual(info, getDefaultVCardData())) {
        return null;
    }

    return mapVCardInfoToApiFormat(info, isNotShowMetroInSearchChecked);
};

export const mapTimeZoneGroupsFromApiFormat = (query: TimeZoneGroupsQuery): TimeZoneGroup[] => {
    const {
        constants: { timezoneGroups }
    } = query;

    return timezoneGroups.map((timezoneGroup, index) => {
        const { groupNick, timezones } = timezoneGroup;

        return {
            id: String(index),
            name: getNameTimeZoneGroupByGroupNick(groupNick),
            timezones: timezones.map(timezone => {
                return {
                    id: String(timezone.id),
                    name: timezone.name
                };
            })
        };
    });
};

const mapRoiGoalIdFromApiFormat = (goalId: string | null, constants: EditCampaignsConstants): string => {
    switch (String(goalId)) {
        case String(constants.meaningfulGoalsOptimizationGoalId):
            return AdditionalGoalIds.allMeaningfulGoals;

        case String(constants.byAllGoalsGoalId):
            return AdditionalGoalIds.all;

        default:
            return String(goalId) || AdditionalGoalIds.all;
    }
};

const getDefaultVcardForEditCampaigns = (): VisitingCardValueEditCampaign => {
    return {
        isShortVersionChecked: false,
        isNotShowMetroInSearchChecked: false,
        info: getDefaultVCardData(),
        vCardId: getRandomId()
    };
};

export const mapDateEditorsDataFromPackageForCampaigns = (
    pricePackage: PricePackageEditorValue
): DatesIntervalEditorState => {
    const dateStart = moment(pricePackage.dateStart);
    const dateNow = moment(Date.now());
    const startDate = dateStart.isAfter(dateNow) ? dateStart : moment(dateNow);
    const dateEnd = moment(pricePackage.dateEnd);

    return {
        startDate: startDate,
        minDate: startDate,
        endDate: dateEnd,
        maxDate: dateEnd
    };
};

export const getDefaultTimeTargeting = (): TimeTargetingValue => {
    return {
        timeBoard: [],
        useWorkingWeekends: false,
        enabledHolidaysMode: false,
        idTimeZone: ''
    };
};
